import React, { useState, useEffect, useCallback } from 'react';
import QuickDrawModal from '@/components/QuickDrawModal';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { HILLS_BANK_CATEGORIES } from '@/lib/hillsBankCategories';
import { HoldingCostsCard } from './HoldingCostsCard';
import { ReceiptsDocumentation } from './ReceiptsDocumentation';
import { PhaseDocsModalTabs } from './PhaseDocsModalVariants';
import FilePreviewModal from './FilePreviewModal';
import { formatDate, formatDateWithTimezone, getTimezoneAbbrev } from '@/lib/dateUtils';
import { PhaseChecklistModal } from './PhaseChecklistModal';
import { getPhaseChecklist } from '@/lib/phaseChecklists';
import { PrePaidExpensesDialog } from './PrePaidExpensesDialog';
import {
  CheckCircle2,
  Circle,
  Package,
  Wrench,
  ClipboardCheck,
  Plus,
  Trash2,
  Upload,
  FileText,
  MoreVertical,
  DollarSign,
  Calendar,
  User,
  Eye,
  Pencil,
  AlertCircle,
  AlertTriangle,
  Wand2,
  RefreshCw,
  Clock,
  X,
  CreditCard,
  Save,
  ChevronRight,
  ChevronDown,
  Play,
  Square,
  RotateCcw,
  FileSpreadsheet,
  Hammer,
  Home,
  Layers,
  Settings,
  Zap,
  PaintBucket,
  Lightbulb,
  Flag,
  FileCheck,
  Download,
  Image,
  Receipt,
  Info,
  BarChart3,
  ShieldCheck,
  ShieldAlert,
  Lock,
  XCircle,
} from 'lucide-react';
import PhaseInspectionsDialog from './PhaseInspectionsDialog';

// v2.7 — Budget line-item categories (Option C table). "labor_material" is the
// combined turnkey category (labor + materials billed together).
const PHASE_CATEGORIES = [
  { id: 'material', label: 'Materials', chip: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  { id: 'labor', label: 'Labor', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  { id: 'labor_material', label: 'Labor & Materials', chip: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
  { id: 'soft_cost', label: 'Soft Costs', chip: 'bg-teal-50 text-teal-700 border-teal-200', dot: 'bg-teal-500' },
];
const categoryOf = (p) => {
  const t = p?.phase_type;
  return (t === 'material' || t === 'labor' || t === 'labor_material' || t === 'soft_cost') ? t : 'labor';
};
const catMeta = (id) => PHASE_CATEGORIES.find((c) => c.id === id) || PHASE_CATEGORIES[1];
const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// v2.9.2 — Per-item DrawReady requirements. Users pick what's required; smart
// defaults apply when unset: labor/labor & materials require W-9, COI, lien
// waiver, final invoice, and (for trades) a passed inspection — BUT items under
// $2,500 skip COI & lien waiver. Materials / soft costs require just an invoice.
const SMALL_ITEM_THRESHOLD = 2500;

function effectiveRequirements(p) {
  const cat = categoryOf(p);
  const isLabor = cat === 'labor' || cat === 'labor_material';
  const amount = (p.estimated_cost || p.actual_cost || 0);
  const small = amount > 0 && amount < SMALL_ITEM_THRESHOLD;
  const def = isLabor
    ? { w9: true, coi: !small, lien: !small, invoice: true, inspection: !!p.requires_inspection }
    : { w9: false, coi: false, lien: false, invoice: true, inspection: false };
  const pick = (flag, d) => (flag === true || flag === false ? flag : d);
  return {
    w9: pick(p.req_w9, def.w9),
    coi: pick(p.req_coi, def.coi),
    lien: pick(p.req_lien, def.lien),
    invoice: pick(p.req_invoice, def.invoice),
    inspection: pick(p.req_inspection, def.inspection),
  };
}

// DrawReady score: only the requirements the user kept ON are counted, plus a
// budget factor (a draw isn't guaranteed if over budget unless contingency has funds).
function computeDrawReadiness(p, contractor, contingencyRemaining = 0) {
  const req = effectiveRequirements(p);
  const est = p.estimated_cost || 0;
  const act = p.actual_cost || 0;
  const overBudget = act > est && est > 0;
  const budgetDone = !overBudget || contingencyRemaining > 0;
  const budgetLabel = !overBudget
    ? 'On / under budget'
    : (contingencyRemaining > 0 ? 'Over budget — covered by contingency' : 'Over budget — no contingency left');
  const coiOk = !!(contractor && contractor.coi_received)
    && (!contractor.coi_expiration || new Date(contractor.coi_expiration) > new Date());
  const hasInvoice = p.has_receipt
    || (Array.isArray(p.receipts) && p.receipts.length > 0)
    || p.is_final_payment || p.payment_status === 'final' || !!p.payment_proof_file_id
    || !!p.receipt_file_id || !!p.invoice_file_id || (parseFloat(p.invoice_amount) > 0);
  const inspPassed = p.inspection_passed || (Array.isArray(p.inspections) && p.inspections.some((i) => i.status === 'passed'));
  const steps = [];
  if (req.w9) steps.push({ key: 'w9', label: 'W-9 on file', done: !!(contractor && contractor.w9_received) });
  if (req.coi) steps.push({ key: 'coi', label: 'Valid COI', done: coiOk });
  if (req.lien) steps.push({ key: 'lien', label: 'Lien waiver signed', done: !!p.lien_waiver_signed });
  if (req.invoice) steps.push({ key: 'invoice', label: 'Invoice uploaded', done: !!hasInvoice });
  if (req.inspection) steps.push({ key: 'inspection', label: 'Passed inspection', done: !!inspPassed });
  steps.push({ key: 'budget', label: budgetLabel, done: budgetDone });
  const done = steps.filter((s) => s.done).length;
  const score = steps.length ? Math.round((done / steps.length) * 100) : 0;
  return { score, ready: score === 100, steps };
}

const REQ_META = [
  { key: 'w9', label: 'W-9 on file' },
  { key: 'coi', label: 'Valid COI' },
  { key: 'lien', label: 'Lien waiver signed' },
  { key: 'invoice', label: 'Invoice uploaded' },
  { key: 'inspection', label: 'Passed inspection' },
];

function DrawReadyChip({ readiness, requirements, onToggleRequirement, editable = false, testId }) {
  const { score, ready, steps } = readiness;
  const doneMap = {};
  steps.forEach((s) => { doneMap[s.key] = s.done; });
  const tone = ready
    ? 'text-green-700 bg-green-50 border-green-300'
    : (score >= 50 ? 'text-amber-700 bg-amber-50 border-amber-300' : 'text-red-600 bg-red-50 border-red-300');
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${tone}`}
          data-testid={testId || 'drawready-chip'}
        >
          <ShieldCheck className="w-3.5 h-3.5" />{ready ? 'DrawReady' : score}
        </button>
      </PopoverTrigger>
      <PopoverContent side="left" className="w-72 p-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-slate-700">Draw readiness</span>
          <span className={`text-xs font-bold ${ready ? 'text-green-600' : 'text-slate-500'}`}>{score}/100</span>
        </div>
        {editable && (
          <p className="text-[11px] text-slate-400 mb-2">Toggle what this item needs. Un-needed steps stop counting.</p>
        )}
        <div className="space-y-1">
          {REQ_META.map((r) => {
            const required = requirements ? requirements[r.key] : true;
            const done = doneMap[r.key];
            return (
              <div key={r.key} className="flex items-center justify-between gap-2 text-xs py-0.5" data-testid={`dr-req-${r.key}`}>
                <span className="flex items-center gap-2">
                  {!required
                    ? <span className="w-4 h-4 shrink-0 text-slate-300 text-center leading-4">–</span>
                    : (done ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" /> : <XCircle className="w-4 h-4 text-red-400 shrink-0" />)}
                  <span className={!required ? 'text-slate-300 line-through' : (done ? 'text-slate-700' : 'text-slate-500')}>{r.label}</span>
                </span>
                {editable && onToggleRequirement && (
                  <button
                    type="button"
                    onClick={() => onToggleRequirement(r.key, !required)}
                    data-testid={`dr-req-toggle-${r.key}`}
                    className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${required ? 'bg-blue-600' : 'bg-slate-300'}`}
                    title={required ? 'Required — tap to skip' : 'Not required — tap to require'}
                  >
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${required ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
                  </button>
                )}
              </div>
            );
          })}
          {/* budget factor (always applies, not toggleable) */}
          {steps.filter((s) => s.key === 'budget').map((s) => (
            <div key="budget" className="flex items-center gap-2 text-xs py-0.5 border-t border-slate-100 mt-1 pt-1.5">
              {s.done ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" /> : <XCircle className="w-4 h-4 text-red-400 shrink-0" />}
              <span className={s.done ? 'text-slate-700' : 'text-slate-500'}>{s.label}</span>
            </div>
          ))}
        </div>
        {ready
          ? <p className="mt-2 text-[11px] text-green-600">All required steps complete — ready to submit for draw.</p>
          : <p className="mt-2 text-[11px] text-slate-400">Complete or skip the remaining items to reach 100.</p>}
      </PopoverContent>
    </Popover>
  );
}


// Phase icons using lucide icons instead of emojis
const PHASE_ICON_COMPONENTS = {
  1: FileSpreadsheet, // Planning
  2: Layers,          // Foundation  
  3: Hammer,          // Framing
  4: Settings,        // Rough-in
  5: Square,          // Insulation/Drywall
  6: PaintBucket,     // Interior
  7: Lightbulb,       // Fixtures
  8: Home,            // Handover
};

const INSPECTION_TYPE_LABELS = {
  permit_application: 'Permit Application',
  permit_review: 'Permit Review',
  erosion_control: 'Erosion Control',
  footing_inspection: 'Footing Inspection',
  foundation_inspection: 'Foundation Inspection',
  framing_inspection: 'Framing Inspection',
  rough_plumbing: 'Rough Plumbing',
  rough_electrical: 'Rough Electrical',
  rough_mechanical: 'Rough Mechanical',
  rough_hvac: 'Rough HVAC',
  insulation_inspection: 'Insulation Inspection',
  final_plumbing: 'Final Plumbing',
  final_electrical: 'Final Electrical',
  final_mechanical: 'Final Mechanical',
  final_building: 'Final Building',
  certificate_of_occupancy: 'Certificate of Occupancy',
  other: 'Other'
};

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'on_hold', label: 'On Hold' }
];

// Compact inline info tooltip for the consolidated All-In card metric tiles.
const MetricInfo = ({ title, children }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button className="hover:bg-white/20 rounded-full p-0.5 transition-colors" data-testid={`metric-info-${(title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
        <Info className="w-2.5 h-2.5 text-cyan-300/70 hover:text-cyan-100" />
      </button>
    </PopoverTrigger>
    <PopoverContent side="top" className="max-w-xs bg-slate-800 text-white p-3 text-xs border-slate-600">
      <p className="font-semibold mb-1">{title}</p>
      <p>{children}</p>
    </PopoverContent>
  </Popover>
);


export const ConstructionPhasesV2 = ({ 
  projectId, 
  project, 
  contractors = [],
  onRefresh,
  onRequestDraw,
  onPayContractor,
  isReadOnly = false,  // Read-only mode for investors and view-only users
  useBatchB = false,  // v2.5.4 — morphing Pay/View-Docs button + inline docs drawer + Start/Complete/Edit row
  inspectionGateEnabled = false,  // v2.5.6 — inspection compliance gate (HVAC/Electrical/Plumbing/Concrete/Framing)
  userId,             // v2.6 — for the peer cost-benchmark fetch
  onRequestCOI        // v2.6 — opens the Request COI dialog (null hides the CTA)
}) => {
  const [constructionPhases, setConstructionPhases] = useState([]);
  const [budgetPhases, setBudgetPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  // v2.6 — peer cost benchmark (per-unit / per-SF vs portfolio average)
  const [benchmark, setBenchmark] = useState(null);
  useEffect(() => {
    let active = true;
    if (!projectId || !userId) return;
    apiService.projectBenchmark(projectId, userId)
      .then((r) => { if (active) setBenchmark(r.data); })
      .catch(() => { if (active) setBenchmark(null); });
    return () => { active = false; };
  }, [projectId, userId]);
  const [expandedPhases, setExpandedPhases] = useState(['phase-1']);
  const [showUnassigned, setShowUnassigned] = useState(true);
  // v2.5.4 (Batch B): inline docs drawer expansion state — keyed by phase.id
  const [expandedDocsPhaseId, setExpandedDocsPhaseId] = useState(null);
  
  // Add Material/Labor Dialog
  const [isAddItemDialogOpen, setIsAddItemDialogOpen] = useState(false);
  const [addItemType, setAddItemType] = useState('material');
  const [addItemPhaseNumber, setAddItemPhaseNumber] = useState(null);
  // v2.7 — budget view toggle (card view vs Option C table) + table category filter
  const [budgetView] = useState('table');
  // Per-line Quick Draw (opened from a line item's action row).
  const [quickDrawPhaseId, setQuickDrawPhaseId] = useState(null);
  // Drawable balance for a line (mirrors QuickDrawModal): actual (or estimate) minus already drawn.
  const qdDrawable = (p) => {
    const base = (parseFloat(p?.actual_cost) || 0) > 0 ? parseFloat(p.actual_cost) : (parseFloat(p?.estimated_cost) || 0);
    return Math.max(0, base - (parseFloat(p?.amount_drawn) || 0));
  };
  const renderQuickDrawBtn = (p) => {
    if (isReadOnly || qdDrawable(p) <= 0.01) return null;
    return (
      <Button
        size="sm"
        variant="outline"
        className="h-7 text-xs border-cyan-500 text-cyan-700 hover:bg-cyan-50"
        onClick={(e) => { e.stopPropagation(); setQuickDrawPhaseId(p.id); }}
        data-testid={`quick-draw-line-${p.id}`}
      >
        <Zap className="w-3 h-3 mr-1" /> Draw
      </Button>
    );
  };
  // v2.9.14 — canonical, always-available "Docs" button. The redesign only
  // exposed the document viewer on fully-paid phases, so anything uploaded to
  // an unpaid item (receipt, W-9, COI, lien waiver, inspection, photos) became
  // unreachable. This opens the full docs modal for ANY item, paid or not.
  const renderDocsBtn = (p) => (
    <Button
      size="sm"
      variant="outline"
      className="h-7 text-xs border-blue-500 text-blue-600 hover:bg-blue-50"
      onClick={(e) => { e.stopPropagation(); setExpandedDocsPhaseId(p.id); }}
      data-testid={`view-docs-btn-${p.id}`}
    >
      <FileText className="w-3 h-3 mr-1" /> Docs
    </Button>
  );
  const [tableCatFilter, setTableCatFilter] = useState('all');
  const [collapsedTablePhases, setCollapsedTablePhases] = useState(() => new Set());
  const toggleTablePhase = (num) => setCollapsedTablePhases((prev) => {
    const next = new Set(prev);
    if (next.has(num)) next.delete(num); else next.add(num);
    return next;
  });
  const [newItem, setNewItem] = useState({
    name: '',
    estimated_cost: '',
    contractor_id: '',
    description: '',
    start_date: '',
    end_date: '',
    supplier_vendor: '',
    vendor: '',
    bank_category: '',
    requires_compliance: false,
  });
  const [addItemReceiptFile, setAddItemReceiptFile] = useState(null);
  const [isExtractingReceipt, setIsExtractingReceipt] = useState(false);
  
  // Edit Item Dialog (Full Edit)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  
  // Inline Edit State
  const [inlineEditId, setInlineEditId] = useState(null);
  const [inlineEditData, setInlineEditData] = useState({});
  
  // Start Date Confirmation Dialog
  const [isStartDialogOpen, setIsStartDialogOpen] = useState(false);
  const [startingItem, setStartingItem] = useState(null);
  const [startDate, setStartDate] = useState('');

  // Mark Complete Confirmation Dialog
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
  const [completingItem, setCompletingItem] = useState(null);
  const [completeDate, setCompleteDate] = useState('');
  
  // Edit Construction Phase Dialog
  const [isEditPhaseDialogOpen, setIsEditPhaseDialogOpen] = useState(false);
  const [editingPhase, setEditingPhase] = useState(null);
  const [editPhaseData, setEditPhaseData] = useState({ name: '', description: '' });
  
  // Add Inspection Dialog
  const [isAddInspectionDialogOpen, setIsAddInspectionDialogOpen] = useState(false);
  const [addInspectionPhaseNumber, setAddInspectionPhaseNumber] = useState(null);

  // v2.5.6: Phase-line-item Inspection Gate dialog
  const [inspectionsPhase, setInspectionsPhase] = useState(null);  // phase obj when open
  const [phaseGateRefreshKey, setPhaseGateRefreshKey] = useState(0);
  const openPhaseInspections = useCallback((p) => setInspectionsPhase(p), []);
  const closePhaseInspections = useCallback(() => {
    setInspectionsPhase(null);
    // Refresh the parent phases so updated requires_inspection / inspections show
    onRefresh?.();
    setPhaseGateRefreshKey((k) => k + 1);
  }, [onRefresh]);
  const onInspectionGateChange = useCallback(() => {
    setPhaseGateRefreshKey((k) => k + 1);
  }, []);
  // Inline toggle: flip `requires_inspection` on a single phase without
  // opening the full edit dialog. Optimistic-updates the local list, persists
  // via PATCH /phases/{id}, and toasts the new state.
  const toggleRequiresInspection = useCallback(async (p) => {
    if (!p?.id) return;
    const next = !p.requires_inspection;
    setBudgetPhases((prev) => prev.map((row) =>
      row.id === p.id ? { ...row, requires_inspection: next } : row
    ));
    try {
      // Preserve contractor_id explicitly — the PATCH endpoint treats an
      // omitted contractor_id as a null write, which would orphan the phase.
      await apiService.updatePhase(p.id, {
        requires_inspection: next,
        contractor_id: p.contractor_id || null,
      });
      toast.success(next
        ? `Inspection now required on "${p.name}"`
        : `Inspection no longer required on "${p.name}"`
      );
    } catch (err) {
      // Roll back optimistic update on failure
      setBudgetPhases((prev) => prev.map((row) =>
        row.id === p.id ? { ...row, requires_inspection: !next } : row
      ));
      toast.error('Failed to update inspection requirement');
    }
  }, []);
  // Compute gate satisfaction for a phase (matches backend _phase_inspection_satisfied)
  const isInspectionSatisfied = (p) => {
    if (!p) return true;
    if (p.inspection_passed) return true;
    if (p.inspection_file_id) return true;
    const items = Array.isArray(p.inspections) ? p.inspections : [];
    return items.some((i) => (i?.status === 'passed'));
  };
  
  // Phase Checklist Modal (75% completion trigger)
  const [isPhaseChecklistOpen, setIsPhaseChecklistOpen] = useState(false);
  const [checklistPhaseNumber, setChecklistPhaseNumber] = useState(null);
  const [checklistPhaseName, setChecklistPhaseName] = useState('');
  // v2.5.10: persist dismissed phase-checklist prompts in localStorage so
  // the "Before Final Walkthrough" (and other phase checklists) don't pop
  // again on every project open / mobile-tab-resume. Keyed per project+phase.
  const _CHECKLIST_DISMISS_KEY = 'drawready.dismissed_phase_checklists.v1';
  const [dismissedChecklists, setDismissedChecklists] = useState(() => {
    try {
      const raw = localStorage.getItem(_CHECKLIST_DISMISS_KEY);
      if (raw) return new Set(JSON.parse(raw));
    } catch (_) { /* malformed — start empty */ }
    return new Set();
  });
  useEffect(() => {
    try {
      localStorage.setItem(_CHECKLIST_DISMISS_KEY, JSON.stringify(Array.from(dismissedChecklists)));
    } catch (_) { /* quota / private mode — non-fatal */ }
  }, [dismissedChecklists]);
  
  // Document Viewer Dialog
  const [isDocViewerOpen, setIsDocViewerOpen] = useState(false);
  const [viewingItemDocs, setViewingItemDocs] = useState(null);
  const [itemDocuments, setItemDocuments] = useState(null);
  // v2.6 — in-app document/photo preview (replaces target="_blank" so users
  // stay inside the app; Download remains available inside the modal).
  const [filePreview, setFilePreview] = useState(null);
  const openPreview = (rawUrl, filename) => {
    if (!rawUrl) return;
    const url = rawUrl.startsWith('http') ? rawUrl : `${process.env.REACT_APP_BACKEND_URL}${rawUrl}`;
    setFilePreview({ url, filename: filename || (url.split('/').pop() || 'Document') });
  };
  const [loadingDocs, setLoadingDocs] = useState(false);

  // Pre-Project Expenses Bulk Import Dialog
  const [isPrePaidDialogOpen, setIsPrePaidDialogOpen] = useState(false);

  // Track whether the user has any pre-paid soft costs in this project
  const hasPrepaidExpenses = budgetPhases.some(
    (p) => p.phase_type === 'soft_cost' && p.is_prepaid,
  );
  
  const [newInspection, setNewInspection] = useState({
    name: '',
    type: 'other',
    notes: ''
  });
  
  // Upload Inspection File
  const [uploadingInspectionId, setUploadingInspectionId] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.getConstructionPhases(projectId);
      setConstructionPhases(res.data.construction_phases || []);
      setBudgetPhases(res.data.phases || []);
    } catch (err) {
      console.error('Failed to load construction phases:', err);
      toast.error('Failed to load construction phases');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-assign on first load if there are unassigned items
  useEffect(() => {
    const autoAssignIfNeeded = async () => {
      if (constructionPhases.length > 0 && budgetPhases.length > 0) {
        const allLinkedIds = constructionPhases.flatMap(cp => cp.linked_phase_ids || []);
        const unassignedCount = budgetPhases.filter(p => !allLinkedIds.includes(p.id)).length;
        
        if (unassignedCount > 0 && unassignedCount === budgetPhases.length) {
          try {
            const res = await apiService.autoAssignConstructionPhases(projectId);
            setConstructionPhases(res.data.construction_phases || constructionPhases);
          } catch (err) {
            console.error('Auto-assign failed:', err);
          }
        }
      }
    };
    autoAssignIfNeeded();
  }, [constructionPhases.length, budgetPhases.length, projectId]);

  // Check for phases reaching 90% and show checklist prompt - ONLY for users with edit permission
  // v2.5.10: raised threshold from 75% to 90% so the modal only fires when
  // the phase is near close-out (not when it's just past halfway). Combined
  // with the persisted dismissedChecklists in localStorage, the prompt now
  // surfaces at most once per project+phase, ever.
  useEffect(() => {
    if (constructionPhases.length === 0) return;
    if (isReadOnly) return; // Don't show checklists for read-only users
    
    // Find phases at or above 90% that haven't been dismissed
    for (const cp of constructionPhases) {
      const progress = cp.progress_percent || 0;
      const phaseKey = `${projectId}-phase-${cp.phase_number}`;
      
      // Check if phase is between 90-99% and hasn't been dismissed
      if (progress >= 90 && progress < 100 && !dismissedChecklists.has(phaseKey)) {
        // Make sure there's a checklist for this phase
        const checklist = getPhaseChecklist(cp.phase_number);
        if (checklist) {
          setChecklistPhaseNumber(cp.phase_number);
          setChecklistPhaseName(cp.name);
          setIsPhaseChecklistOpen(true);
          // Mark as shown so we don't show again (persists via localStorage effect above)
          setDismissedChecklists(prev => new Set([...prev, phaseKey]));
          break; // Only show one at a time
        }
      }
    }
  }, [constructionPhases, projectId, dismissedChecklists, isReadOnly]);

  // Auto-prompt for Pre-Project Expenses on first project visit.
  // Triggers once globally per browser when there are no pre-paid soft costs
  // and the user hasn't already been prompted. Skipped for projects that are
  // already past the planning stage (i.e., have phase start dates set).
  useEffect(() => {
    if (isReadOnly || loading || !projectId) return;
    if (hasPrepaidExpenses) return;
    // Global flag — once dismissed on any project, never auto-open again.
    if (localStorage.getItem('prepaid-prompt-shown-global') === 'true') return;
    // Per-project flag (legacy)
    if (localStorage.getItem(`prepaid-prompt-shown-${projectId}`) === 'true') return;
    // Skip if project is past planning — any phase with a start_date or any
    // construction phase with allocated budget means the user already moved on.
    const projectIsActive =
      (constructionPhases || []).some((cp) => Number(cp.budget_allocated || 0) > 0) ||
      (budgetPhases || []).some((p) => p.start_date);
    if (projectIsActive) return;

    const timer = setTimeout(() => {
      setIsPrePaidDialogOpen(true);
      localStorage.setItem(`prepaid-prompt-shown-${projectId}`, 'true');
      localStorage.setItem('prepaid-prompt-shown-global', 'true');
    }, 1200);
    return () => clearTimeout(timer);
  }, [projectId, isReadOnly, loading, hasPrepaidExpenses, constructionPhases, budgetPhases]);

  // Scroll-to-phase: when URL hash is #phase-{phase_id}, expand the right
  // construction phase accordion and scroll to that phase row + briefly highlight.
  // Listens to BOTH initial mount and hashchange events so it works even when
  // the user is already on the project page and the hash gets updated.
  useEffect(() => {
    if (loading) return;
    const triggerScroll = () => {
      const hash = (typeof window !== 'undefined' && window.location.hash) || '';
      const m = hash.match(/^#phase-([a-zA-Z0-9-]+)/);
      if (!m) return;
      const targetPhaseId = m[1];
      if (!targetPhaseId) return;

      // Find which construction phase contains this phase id
      const cp = (constructionPhases || []).find((c) =>
        (c.linked_phase_ids || []).includes(targetPhaseId),
      );
      if (cp) {
        setExpandedPhases((prev) =>
          prev.includes(`phase-${cp.phase_number}`) ? prev : [...prev, `phase-${cp.phase_number}`],
        );
      }

      // Wait for accordion to expand, then scroll + highlight
      setTimeout(() => {
        const el = document.getElementById(`phase-row-${targetPhaseId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-4', 'ring-amber-400', 'ring-offset-2');
          setTimeout(() => {
            el.classList.remove('ring-4', 'ring-amber-400', 'ring-offset-2');
            if (window.history.replaceState) {
              window.history.replaceState(null, '', window.location.pathname + window.location.search);
            }
          }, 2400);
        }
      }, 500);
    };

    triggerScroll();
    window.addEventListener('hashchange', triggerScroll);
    return () => window.removeEventListener('hashchange', triggerScroll);
  }, [loading, constructionPhases, budgetPhases]);

  const handleAutoAssign = async () => {
    try {
      const res = await apiService.autoAssignConstructionPhases(projectId);
      setConstructionPhases(res.data.construction_phases || constructionPhases);
      toast.success(res.data.message);
    } catch (err) {
      toast.error('Failed to auto-assign phases');
    }
  };

  const handleAddItem = async () => {
    if (!newItem.name || !newItem.estimated_cost) {
      toast.error('Name and estimated cost are required');
      return;
    }

    // Require contractor for labor items
    if ((addItemType === 'labor' || addItemType === 'labor_material') && !newItem.contractor_id) {
      toast.error('Please assign a contractor before proceeding');
      return;
    }

    try {
      const phaseData = {
        project_id: projectId,
        name: newItem.name,
        estimated_cost: parseFloat(newItem.estimated_cost) || 0,
        phase_type: addItemType,
        description: newItem.description,
        contractor_id: newItem.contractor_id || null,
        supplier_vendor: (addItemType === 'material' || addItemType === 'labor_material') ? newItem.supplier_vendor : null,
        vendor: addItemType === 'soft_cost' ? newItem.vendor : null,
        start_date: newItem.start_date ? new Date(newItem.start_date).toISOString() : null,
        end_date: newItem.end_date ? new Date(newItem.end_date).toISOString() : null,
        bank_category: newItem.bank_category || null,
        // v2.4: soft cost compliance toggle. For non-soft-cost types this is ignored.
        requires_compliance: addItemType === 'soft_cost' ? !!newItem.requires_compliance : undefined,
      };

      const createRes = await apiService.createPhase(phaseData);
      let newPhase = createRes.data;

      // If a receipt file was scanned, upload it now that we have the phase ID
      if (addItemReceiptFile && newPhase.id) {
        try {
          const uploadRes = await apiService.uploadPhaseReceiptFile(newPhase.id, addItemReceiptFile);
          newPhase = { 
            ...newPhase, 
            receipt_file_id: uploadRes.data.file_id, 
            receipt_filename: uploadRes.data.filename 
          };
        } catch (uploadErr) {
          console.error('Receipt file upload failed:', uploadErr);
          // Continue anyway - item was created
        }
      }

      // Link to the construction phase
      await apiService.linkPhaseToConstruction(projectId, addItemPhaseNumber, newPhase.id);

      // Update local state instead of reloading
      setBudgetPhases(prev => [...prev, newPhase]);
      setConstructionPhases(prev => prev.map(cp => {
        if (cp.phase_number === addItemPhaseNumber) {
          return {
            ...cp,
            linked_phase_ids: [...(cp.linked_phase_ids || []), newPhase.id]
          };
        }
        return cp;
      }));

      toast.success(`${addItemType === 'material' ? 'Material' : addItemType === 'soft_cost' ? 'Soft Cost' : addItemType === 'labor_material' ? 'Labor & Materials' : 'Labor'} added successfully`);
      setIsAddItemDialogOpen(false);
      setNewItem({ name: '', estimated_cost: '', contractor_id: '', description: '', start_date: '', end_date: '', supplier_vendor: '', vendor: '', bank_category: '', requires_compliance: false });
      setAddItemReceiptFile(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to add item:', err);
      toast.error('Failed to add item');
    }
  };

  // AI Receipt Extraction for Add Item dialog
  const handleExtractReceiptForAddItem = async (file) => {
    if (!file) return;
    
    setIsExtractingReceipt(true);
    setAddItemReceiptFile(file);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await apiService.extractReceiptData(formData);
      
      if (res.data.success && res.data.extracted_data) {
        const data = res.data.extracted_data;
        
        // Try to match contractor from extracted vendor name
        let matchedContractorId = '';
        if (data.vendor_name && contractors.length > 0) {
          const vendorLower = data.vendor_name.toLowerCase();
          const matchedContractor = contractors.find(c => {
            const companyLower = (c.company || '').toLowerCase();
            const nameLower = (c.name || '').toLowerCase();
            return companyLower.includes(vendorLower) || 
                   vendorLower.includes(companyLower) ||
                   nameLower.includes(vendorLower) ||
                   vendorLower.includes(nameLower);
          });
          if (matchedContractor) {
            matchedContractorId = matchedContractor.id;
          }
        }
        
        // Auto-fill the form with extracted data
        setNewItem(prev => ({
          ...prev,
          name: data.description || data.vendor_name || prev.name,
          estimated_cost: data.total_amount || prev.estimated_cost,
          supplier_vendor: data.vendor_name || prev.supplier_vendor,
          contractor_id: matchedContractorId || prev.contractor_id,
          description: data.line_items 
            ? data.line_items.map(item => `${item.description}: $${item.amount}`).join('\n')
            : (data.description || prev.description),
          start_date: data.date || prev.start_date,
          bank_category: data.suggested_bank_category || prev.bank_category
        }));
        
        toast.success('Receipt data extracted successfully!');
      } else {
        toast.error(res.data.error || 'Could not extract data from receipt');
      }
    } catch (err) {
      console.error('Receipt extraction failed:', err);
      toast.error('Failed to extract receipt data');
    } finally {
      setIsExtractingReceipt(false);
    }
  };

  // Open Full Edit Dialog
  const openFullEditDialog = (item) => {
    setEditingItem(item);
    setEditFormData({
      name: item.name || '',
      phase_type: item.phase_type || 'labor',
      estimated_cost: item.estimated_cost || 0,
      actual_cost: item.actual_cost || 0,
      return_refund_amount: item.return_refund_amount || 0,
      description: item.description || '',
      contractor_id: item.contractor_id || '__none__',
      supplier_vendor: item.supplier_vendor || '',
      start_date: item.start_date ? item.start_date.split('T')[0] : '',
      end_date: item.end_date ? item.end_date.split('T')[0] : '',
      status: item.status || 'pending',
      bank_category: item.bank_category || '__none__',
      // Pre-paid fields for soft costs
      is_prepaid: item.is_prepaid || false,
      prepaid_amount: item.prepaid_amount || item.actual_cost || 0,
      prepaid_date: item.prepaid_date ? item.prepaid_date.split('T')[0] : '',
      is_paid: item.is_paid || false
    });
    setIsEditDialogOpen(true);
  };

  // Save Full Edit - Update local state instead of reloading
  const handleSaveFullEdit = async () => {
    if (!editingItem) return;
    
    try {
      // Convert __none__ sentinel values back to null for API
      const contractor_id = editFormData.contractor_id === '__none__' ? null : (editFormData.contractor_id || null);
      const bank_category = editFormData.bank_category === '__none__' ? null : (editFormData.bank_category || null);
      
      const updateData = {
        name: editFormData.name,
        phase_type: editFormData.phase_type || 'labor',
        estimated_cost: parseFloat(editFormData.estimated_cost) || 0,
        actual_cost: parseFloat(editFormData.actual_cost) || 0,
        return_refund_amount: parseFloat(editFormData.return_refund_amount) || 0,
        description: editFormData.description,
        contractor_id: contractor_id,
        supplier_vendor: editFormData.supplier_vendor || null,
        start_date: editFormData.start_date ? new Date(editFormData.start_date).toISOString() : null,
        end_date: editFormData.end_date ? new Date(editFormData.end_date).toISOString() : null,
        status: editFormData.status,
        bank_category: bank_category,
        // Pre-paid fields
        is_prepaid: editFormData.is_prepaid || false,
        prepaid_amount: editFormData.is_prepaid ? (parseFloat(editFormData.prepaid_amount) || 0) : null,
        prepaid_date: editFormData.is_prepaid && editFormData.prepaid_date ? new Date(editFormData.prepaid_date).toISOString() : null,
        is_paid: editFormData.is_prepaid ? true : (editFormData.is_paid || false)
      };

      await apiService.updatePhase(editingItem.id, updateData);
      
      // Update local state
      setBudgetPhases(prev => prev.map(p => 
        p.id === editingItem.id ? { ...p, ...updateData } : p
      ));
      
      toast.success('Item updated successfully');
      setIsEditDialogOpen(false);
      setEditingItem(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to update item:', err);
      toast.error('Failed to update item');
    }
  };

  // v2.9.2 — toggle a single DrawReady compliance requirement for a line item.
  const REQ_FIELD = { w9: 'req_w9', coi: 'req_coi', lien: 'req_lien', invoice: 'req_invoice', inspection: 'req_inspection' };
  const updateItemRequirement = async (phase, key, value) => {
    const field = REQ_FIELD[key];
    if (!field) return;
    try {
      await apiService.updatePhase(phase.id, { [field]: value });
      setBudgetPhases((prev) => prev.map((p) => (p.id === phase.id ? { ...p, [field]: value } : p)));
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Failed to update requirement');
    }
  };


  // Start Inline Edit
  const startInlineEdit = (item) => {
    setInlineEditId(item.id);
    setInlineEditData({
      estimated_cost: item.estimated_cost || 0,
      actual_cost: item.actual_cost || 0,
      return_refund_amount: item.return_refund_amount || 0,
      start_date: item.start_date ? item.start_date.split('T')[0] : '',
      end_date: item.end_date ? item.end_date.split('T')[0] : '',
      status: item.status || 'pending'
    });
  };

  // Save Inline Edit - Update local state instead of reloading
  const saveInlineEdit = async (itemId) => {
    try {
      const updateData = {
        estimated_cost: parseFloat(inlineEditData.estimated_cost) || 0,
        actual_cost: parseFloat(inlineEditData.actual_cost) || 0,
        return_refund_amount: parseFloat(inlineEditData.return_refund_amount) || 0,
        start_date: inlineEditData.start_date ? new Date(inlineEditData.start_date).toISOString() : null,
        end_date: inlineEditData.end_date ? new Date(inlineEditData.end_date).toISOString() : null,
        status: inlineEditData.status
      };

      await apiService.updatePhase(itemId, updateData);
      
      // Update local state
      setBudgetPhases(prev => prev.map(p => 
        p.id === itemId ? { ...p, ...updateData } : p
      ));
      
      toast.success('Saved');
      setInlineEditId(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to save:', err);
      toast.error('Failed to save changes');
    }
  };

  // Cancel Inline Edit
  const cancelInlineEdit = () => {
    setInlineEditId(null);
    setInlineEditData({});
  };

  // Open Start Date Dialog
  const openStartDialog = (item) => {
    setStartingItem(item);
    setStartDate(new Date().toISOString().split('T')[0]);
    setIsStartDialogOpen(true);
  };

  // Confirm Start with Date
  const confirmStart = async () => {
    if (!startingItem) return;
    
    try {
      const updateData = {
        status: 'in_progress',
        start_date: startDate ? new Date(startDate).toISOString() : new Date().toISOString()
      };
      
      await apiService.updatePhase(startingItem.id, updateData);
      
      // Update local state
      setBudgetPhases(prev => prev.map(p => 
        p.id === startingItem.id ? { ...p, ...updateData } : p
      ));
      
      toast.success(`${startingItem.name} started`);
      setIsStartDialogOpen(false);
      setStartingItem(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Failed to start item');
    }
  };

  // Open Mark Complete dialog (date picker, defaults to today)
  const openCompleteDialog = (item) => {
    setCompletingItem(item);
    setCompleteDate(new Date().toISOString().split('T')[0]);
    setIsCompleteDialogOpen(true);
  };

  // Confirm Mark Complete with chosen date
  const confirmComplete = async () => {
    if (!completingItem) return;
    try {
      const updateData = {
        status: 'completed',
        end_date: completeDate ? new Date(completeDate).toISOString() : new Date().toISOString(),
      };
      await apiService.updatePhase(completingItem.id, updateData);
      setBudgetPhases(prev => prev.map(p =>
        p.id === completingItem.id ? { ...p, ...updateData } : p
      ));
      toast.success(`${completingItem.name} completed`);
      setIsCompleteDialogOpen(false);
      setCompletingItem(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Failed to complete item');
    }
  };

  // Undo / Reopen a phase's schedule state.
  //   completed   -> back to in_progress (clears end_date)
  //   in_progress -> back to not started (clears start_date & end_date)
  const handleReopenPhase = async (item) => {
    if (!item) return;
    const wasCompleted = item.status === 'completed';
    const updateData = wasCompleted
      ? { status: 'in_progress', end_date: null }
      : { status: 'not_started', start_date: null, end_date: null };
    try {
      await apiService.updatePhase(item.id, updateData);
      setBudgetPhases(prev => prev.map(p =>
        p.id === item.id ? { ...p, ...updateData } : p
      ));
      toast.success(wasCompleted ? `${item.name} reopened — back to In progress` : `${item.name} reset — not started`);
      if (onRefresh) onRefresh();
      await loadData();  // reconcile from the backend so the table reflects the reopen
    } catch (err) {
      toast.error('Failed to reopen item');
    }
  };

  // View Item Documents (lien waiver, receipts, photos)
  const handleViewDocuments = async (item) => {
    setViewingItemDocs(item);
    setLoadingDocs(true);
    setIsDocViewerOpen(true);
    
    try {
      console.log('Loading documents for item:', item.id, item.name);
      
      // Try compliance-documents endpoint first
      let docs = null;
      try {
        docs = await apiService.getPhaseComplianceDocuments(item.id);
        console.log('compliance-documents response:', docs);
      } catch (err) {
        console.log('compliance-documents endpoint failed:', err.message);
      }
      
      // Also get compliance-status which has photo/lien waiver info
      let status = null;
      try {
        status = await apiService.getPhaseComplianceStatus(item.id);
        console.log('compliance-status response:', status);
      } catch (err) {
        console.log('compliance-status endpoint failed:', err.message);
      }
      
      // Merge data from both sources
      const mergedDocs = {
        phase_id: item.id,
        phase_name: item.name,
        completion_photos: docs?.completion_photos || [],
        lien_waiver: docs?.lien_waiver || null,
        inspection_report: docs?.inspection_report || null,
        receipts: docs?.receipts || [],
        // Add status info
        compliance_status: status?.compliance || null
      };
      
      // If compliance-status shows photos uploaded but we don't have them, show the count
      if (!mergedDocs.completion_photos?.length && status?.compliance?.completion_photos?.uploaded) {
        mergedDocs.photos_uploaded_count = status.compliance.completion_photos.count;
      }
      
      // If compliance-status shows lien waiver signed but we don't have it
      if (!mergedDocs.lien_waiver && status?.compliance?.lien_waiver?.signed) {
        mergedDocs.lien_waiver = {
          type: 'signed',
          signed: true,
          amount: status.compliance.lien_waiver.amount
        };
      }
      
      console.log('Final merged documents:', mergedDocs);
      setItemDocuments(mergedDocs);
    } catch (err) {
      console.error('Failed to load documents:', err);
      toast.error('Failed to load documents');
      setItemDocuments(null);
    } finally {
      setLoadingDocs(false);
    }
  };

  // Check if item has any documents attached
  const hasDocuments = (item) => {
    return item.lien_waiver_signed || 
           item.receipt_url || 
           item.photo_url || 
           (item.documents && item.documents.length > 0) ||
           (item.attachments && item.attachments.length > 0);
  };

  // Open Edit Construction Phase Dialog
  const openEditPhaseDialog = (cp) => {
    setEditingPhase(cp);
    setEditPhaseData({
      name: cp.name || '',
      description: cp.description || ''
    });
    setIsEditPhaseDialogOpen(true);
  };

  // Save Construction Phase Edit
  const handleSavePhaseEdit = async () => {
    if (!editingPhase) return;
    
    try {
      const res = await apiService.updateConstructionPhase(projectId, editingPhase.phase_number, {
        name: editPhaseData.name,
        description: editPhaseData.description
      });
      
      // Update local state
      setConstructionPhases(res.data.construction_phases || constructionPhases);
      
      toast.success('Phase updated');
      setIsEditPhaseDialogOpen(false);
      setEditingPhase(null);
    } catch (err) {
      toast.error('Failed to update phase');
    }
  };

  const handleAddInspection = async () => {
    if (!newInspection.name) {
      toast.error('Inspection name is required');
      return;
    }

    try {
      const res = await apiService.addInspection(projectId, addInspectionPhaseNumber, newInspection);
      
      // Update local state
      setConstructionPhases(prev => prev.map(cp => {
        if (cp.phase_number === addInspectionPhaseNumber) {
          return {
            ...cp,
            inspections: [...(cp.inspections || []), res.data.inspection]
          };
        }
        return cp;
      }));
      
      toast.success('Inspection added');
      setIsAddInspectionDialogOpen(false);
      setNewInspection({ name: '', type: 'other', notes: '' });
    } catch (err) {
      toast.error('Failed to add inspection');
    }
  };

  const handleUploadInspectionFile = async (phaseNumber, inspectionId, file) => {
    try {
      setUploadingInspectionId(inspectionId);
      const res = await apiService.uploadInspectionFile(projectId, phaseNumber, inspectionId, file);
      
      // Update local state
      setConstructionPhases(res.data.construction_phases || constructionPhases);
      
      toast.success('Inspection report uploaded and marked as passed');
    } catch (err) {
      toast.error('Failed to upload inspection file');
    } finally {
      setUploadingInspectionId(null);
    }
  };

  const handleOverrideInspection = async (phaseNumber, inspectionId) => {
    try {
      const res = await apiService.updateInspection(projectId, phaseNumber, inspectionId, { override: true });
      
      // Update local state
      setConstructionPhases(res.data.construction_phases || constructionPhases);
      
      toast.success('Inspection marked as passed (override)');
    } catch (err) {
      toast.error('Failed to update inspection');
    }
  };

  const handleDeleteInspection = async (phaseNumber, inspectionId) => {
    try {
      await apiService.deleteInspection(projectId, phaseNumber, inspectionId);
      
      // Update local state
      setConstructionPhases(prev => prev.map(cp => {
        if (cp.phase_number === phaseNumber) {
          return {
            ...cp,
            inspections: (cp.inspections || []).filter(i => i.id !== inspectionId)
          };
        }
        return cp;
      }));
      
      toast.success('Inspection deleted');
    } catch (err) {
      toast.error('Failed to delete inspection');
    }
  };

  // Quick "Mark as Paid" — used by 3-dot menu and inline card button.
  // Backend creates a synthetic payment ledger entry so the dashboard tile
  // jumps and Pay More dialog shows the entry.
  const handleQuickMarkPaid = async (p) => {
    try {
      await apiService.quickMarkPhasePaid(p.id, project?.user_id);
      toast.success(`${p.name || 'Item'} marked as paid`);
      // Optimistic UI update so the chip flips immediately
      setConstructionPhases(prev => prev.map(cp => ({
        ...cp,
        linked_phases: (cp.linked_phases || []).map(lp =>
          lp.id === p.id
            ? { ...lp, is_paid: true, supplier_paid: true,
                supplier_amount_paid: lp.actual_cost || lp.estimated_cost || 0 }
            : lp
        ),
      })));
      if (typeof onRefresh === 'function') onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const handleQuickUnmarkPaid = async (p) => {
    if (!window.confirm(`Reopen "${p.name}"? This keeps all payment records — it just reopens the phase so you can request a lien waiver, pay more, or make corrections.`)) return;
    try {
      await apiService.quickUnmarkPhasePaid(p.id, project?.user_id);
      toast.success('Phase reopened');
      setConstructionPhases(prev => prev.map(cp => ({
        ...cp,
        linked_phases: (cp.linked_phases || []).map(lp =>
          lp.id === p.id
            ? { ...lp, is_paid: false, supplier_paid: false }
            : lp
        ),
      })));
      if (typeof onRefresh === 'function') onRefresh();
      await loadData();  // reconcile from the backend so the row actually reflects the reopen
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to unmark');
    }
  };

  const handleUnlinkPhase = async (phaseNumber, budgetPhaseId) => {
    try {
      await apiService.unlinkPhaseFromConstruction(projectId, phaseNumber, budgetPhaseId);
      
      // Update local state
      setConstructionPhases(prev => prev.map(cp => {
        if (cp.phase_number === phaseNumber) {
          return {
            ...cp,
            linked_phase_ids: (cp.linked_phase_ids || []).filter(id => id !== budgetPhaseId)
          };
        }
        return cp;
      }));
      
      toast.success('Item unlinked');
    } catch (err) {
      toast.error('Failed to unlink item');
    }
  };

  const handleDeletePhase = async (phaseId, skipConfirm = false) => {
    if (!skipConfirm && !window.confirm('Are you sure you want to delete this item? This action cannot be undone.')) {
      return;
    }
    
    try {
      await apiService.deletePhase(phaseId);
      
      // Update local state - remove from budget phases
      setBudgetPhases(prev => prev.filter(p => p.id !== phaseId));
      
      // Also remove from construction phases linked_phase_ids
      setConstructionPhases(prev => prev.map(cp => ({
        ...cp,
        linked_phase_ids: (cp.linked_phase_ids || []).filter(id => id !== phaseId)
      })));
      
      toast.success('Item deleted');
      
      // Refresh to ensure consistency
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to delete phase:', err);
      toast.error('Failed to delete item');
    }
  };

  const handleLinkPhase = async (phaseNumber, budgetPhaseId) => {
    try {
      await apiService.linkPhaseToConstruction(projectId, phaseNumber, budgetPhaseId);
      
      // Update local state - remove from all other phases first, then add to target
      setConstructionPhases(prev => prev.map(cp => {
        const linkedIds = (cp.linked_phase_ids || []).filter(id => id !== budgetPhaseId);
        if (cp.phase_number === phaseNumber) {
          return { ...cp, linked_phase_ids: [...linkedIds, budgetPhaseId] };
        }
        return { ...cp, linked_phase_ids: linkedIds };
      }));
      
      toast.success('Item linked');
    } catch (err) {
      toast.error('Failed to link item');
    }
  };

  // Assign a budget phase item to Holding Costs
  const handleAssignToHoldingCosts = async (budgetPhase) => {
    try {
      // Determine holding cost category based on item name/type
      let category = 'other';
      const nameLower = (budgetPhase.name || '').toLowerCase();
      if (nameLower.includes('dumpster')) category = 'dumpster';
      else if (nameLower.includes('util')) category = 'utilities';
      else if (nameLower.includes('delivery')) category = 'delivery_fees';
      else if (nameLower.includes('clean')) category = 'cleaning';
      else if (nameLower.includes('toilet') || nameLower.includes('porta')) category = 'portable_toilet';
      else if (nameLower.includes('secur') || nameLower.includes('fence')) category = 'security_fencing';
      else if (nameLower.includes('power') || nameLower.includes('electric')) category = 'temporary_power';
      else if (nameLower.includes('insur')) category = 'insurance';
      else if (nameLower.includes('tax')) category = 'property_taxes';
      else if (nameLower.includes('storage')) category = 'storage';
      
      // Create holding cost from budget phase
      const holdingCostData = {
        category,
        name: budgetPhase.name,
        vendor: budgetPhase.supplier_vendor || null,
        bank_category: budgetPhase.bank_category || null,
        estimated_cost: budgetPhase.estimated_cost || 0,
        actual_cost: budgetPhase.actual_cost || 0,
        is_recurring: false
      };
      
      await apiService.createHoldingCost(projectId, holdingCostData);
      
      // Remove from budget phases
      setBudgetPhases(prev => prev.filter(p => p.id !== budgetPhase.id));
      
      // Also delete the original phase from backend
      try {
        await apiService.deletePhase(budgetPhase.id);
      } catch (err) {
        console.error('Failed to delete original phase:', err);
      }
      
      toast.success(`"${budgetPhase.name}" moved to Holding Costs`);
    } catch (err) {
      console.error('Failed to assign to holding costs:', err);
      toast.error('Failed to move item to Holding Costs');
    }
  };

  // Get phases not assigned to any construction phase
  const getUnassignedPhases = () => {
    const allLinkedIds = constructionPhases.flatMap(cp => cp.linked_phase_ids || []);
    return budgetPhases.filter(p => !allLinkedIds.includes(p.id));
  };

  // Calculate totals
  // v2.5.16 — "Total Budget" now reflects the project-level declared
  // budget (purchase price + acquisition + closing + construction +
  // contingency + insurance), not just the sum of phase estimated_costs.
  // Reported on prod: owner edited the project's budget breakdown to
  // $416,411 (48k land + 931 closing + 350,455 construction + 16k
  // contingency + 1,025 insurance) but the Budget & Progress page kept
  // showing $377,314.78 because this calc only summed phase estimated_costs
  // and ignored the project-level fields. Falls back to the phase rollup
  // when the project breakdown is missing (legacy projects).
  const projectTotalBudget = (parseFloat(project?.purchase_price_home) || 0)
    + (parseFloat(project?.purchase_price_land) || 0)
    + (parseFloat(project?.acquisition_closing_costs) || 0)
    + (parseFloat(project?.construction_budget) || 0)
    + (parseFloat(project?.contingency_budget) || 0)
    + (parseFloat(project?.insurance) || 0);
  const totalBudget = projectTotalBudget > 0
    ? projectTotalBudget
    : budgetPhases.reduce((sum, p) => sum + (p.estimated_cost || 0), 0);
  // Calculate net actual cost (actual - returns) for materials
  const totalActual = budgetPhases.reduce((sum, p) => {
    const actualCost = p.actual_cost || 0;
    const returnAmount = p.phase_type === 'material' ? (p.return_refund_amount || 0) : 0;
    return sum + actualCost - returnAmount;
  }, 0);
  const totalPaid = budgetPhases.reduce((sum, p) => {
    if (p.phase_type === 'material') {
      // For materials, use supplier_amount_paid if available, otherwise actual_cost if supplier_paid
      return sum + (p.supplier_amount_paid || (p.supplier_paid ? (p.actual_cost || 0) : 0));
    }
    // For labor, use supplier_amount_paid if available, otherwise actual_cost if is_paid
    return sum + (p.supplier_amount_paid || (p.is_paid ? (p.actual_cost || 0) : 0));
  }, 0);

  // Overall progress
  const overallProgress = constructionPhases.length > 0
    ? Math.round(constructionPhases.reduce((sum, cp) => sum + (cp.progress_percent || 0), 0) / constructionPhases.length)
    : 0;

  const getProgressColor = (percent) => {
    if (percent >= 100) return 'bg-green-500';
    if (percent >= 50) return 'bg-blue-500';
    if (percent > 0) return 'bg-amber-500';
    return 'bg-slate-300';
  };

  // Calculate timeline progress
  const getTimelineProgress = () => {
    if (!project?.start_date) return 0;
    const start = new Date(project.start_date);
    const end = project.projected_completion_date ? new Date(project.projected_completion_date) : new Date(start.getTime() + 180 * 24 * 60 * 60 * 1000);
    const now = new Date();
    const totalDuration = end - start;
    const elapsed = now - start;
    return Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
  };

  // Get bank category label
  const getBankCategoryLabel = (code) => {
    if (!code) return null;
    const cat = HILLS_BANK_CATEGORIES.find(c => c.fullName === code || c.code === code);
    return cat ? cat.fullName : code;
  };

  // Render Material Item Card
  const renderMaterialItem = (p, cpPhaseNumber) => {
    // v2.4: inline quick-edit card removed — clicking opens Full Edit drawer.
    const isEditing = false;
    
    if (isEditing) {
      return (
        <div key={p.id} className="bg-white rounded-lg p-3 shadow-sm border-2 border-amber-400">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-sm">{p.name}</span>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => saveInlineEdit(p.id)}>
                <Save className="w-4 h-4 text-green-600" />
              </Button>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={cancelInlineEdit}>
                <X className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <Label className="text-xs">Est. Cost</Label>
              <Input
                type="number"
                className="h-7 text-xs"
                value={inlineEditData.estimated_cost}
                onChange={(e) => setInlineEditData({...inlineEditData, estimated_cost: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-xs">Actual Cost</Label>
              <Input
                type="number"
                className="h-7 text-xs"
                value={inlineEditData.actual_cost}
                onChange={(e) => setInlineEditData({...inlineEditData, actual_cost: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-xs text-green-600">Return/Refund</Label>
              <Input
                type="number"
                className="h-7 text-xs border-green-300 focus:border-green-500"
                placeholder="0.00"
                value={inlineEditData.return_refund_amount || ''}
                onChange={(e) => setInlineEditData({...inlineEditData, return_refund_amount: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-xs">Net Cost</Label>
              <div className="h-7 flex items-center text-xs font-medium text-slate-700 bg-slate-100 rounded px-2">
                ${((parseFloat(inlineEditData.actual_cost) || 0) - (parseFloat(inlineEditData.return_refund_amount) || 0)).toLocaleString()}
              </div>
            </div>
            <div>
              <Label className="text-xs">Start Date</Label>
              <Input
                type="date"
                className="h-7 text-xs"
                value={inlineEditData.start_date}
                onChange={(e) => setInlineEditData({...inlineEditData, start_date: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-xs">End Date</Label>
              <Input
                type="date"
                className="h-7 text-xs"
                value={inlineEditData.end_date}
                onChange={(e) => setInlineEditData({...inlineEditData, end_date: e.target.value})}
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <React.Fragment key={p.id}>
      <div 
        id={`phase-row-${p.id}`}
        data-phase-id={p.id}
        className="bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
        onClick={() => openFullEditDialog(p)}
      >
        <div className="flex items-center justify-between">
          <span className="font-medium text-sm truncate flex-1">{p.name}</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openFullEditDialog(p); }}>
                <Pencil className="w-4 h-4 mr-2" /> Full Edit
              </DropdownMenuItem>
              {p.status !== 'in_progress' && p.status !== 'completed' && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openStartDialog(p); }}>
                  <Play className="w-4 h-4 mr-2" /> Start
                </DropdownMenuItem>
              )}
              {p.status === 'in_progress' && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openCompleteDialog(p); }}>
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Mark Complete
                </DropdownMenuItem>
              )}
              {(p.status === 'in_progress' || p.status === 'completed') && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleReopenPhase(p); }}>
                  <RotateCcw className="w-4 h-4 mr-2" /> {p.status === 'completed' ? 'Reopen (undo complete)' : 'Undo start'}
                </DropdownMenuItem>
              )}
              {/* v2.4: "Mark as Paid" / "Unmark as Paid" removed — use the
                  Pay button which opens the new Log Payment drawer instead.
                  v2.5.3: "View Documents" removed — fully-paid phases expose
                  a dedicated "View Docs" button next to the Paid badge. */}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleUnlinkPhase(cpPhaseNumber, p.id); }}>
                <X className="w-4 h-4 mr-2" /> Unlink
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={(e) => { e.stopPropagation(); handleDeletePhase(p.id); }}
                className="text-red-600"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center justify-between mt-1 text-xs text-slate-500">
          <span>Est: ${(p.estimated_cost || 0).toLocaleString()}</span>
          {p.supplier_vendor && <span>{p.supplier_vendor}</span>}
        </div>
        {(project?.units || project?.sqft) && (p.estimated_cost > 0) ? (
          <div className="text-[10px] text-slate-400 mt-0.5" data-testid={`phase-item-permetrics-${p.id}`}>
            {project?.units ? `$${Math.round((p.estimated_cost || 0) / project.units).toLocaleString()}/unit` : ''}
            {project?.units && project?.sqft ? ' · ' : ''}
            {project?.sqft ? `$${((p.estimated_cost || 0) / project.sqft).toFixed(2)}/SF` : ''}
          </div>
        ) : null}
        {/* Actual Cost & Return/Refund */}
        {(p.actual_cost > 0 || p.return_refund_amount > 0) && (
          <div className="flex items-center gap-3 mt-1 text-xs">
            <span className="text-slate-600">Actual: ${(p.actual_cost || 0).toLocaleString()}</span>
            {p.return_refund_amount > 0 && (
              <>
                <span className="text-green-600">-${(p.return_refund_amount || 0).toLocaleString()} refund</span>
                <span className="font-medium text-slate-700">Net: ${((p.actual_cost || 0) - (p.return_refund_amount || 0)).toLocaleString()}</span>
              </>
            )}
          </div>
        )}
        {/* Bank Category Badge */}
        {p.bank_category && (
          <div className="mt-1">
            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
              {getBankCategoryLabel(p.bank_category)}
            </Badge>
          </div>
        )}
        {p.start_date && (
          <div className="text-xs text-slate-400 mt-1">
            <Calendar className="w-3 h-3 inline mr-1" />
            {formatDate(p.start_date)}
            {p.end_date && ` - ${formatDate(p.end_date)}`}
          </div>
        )}
        <div className="flex items-center gap-2 mt-1">
          {/* Payment Status Badge for Materials - Show partial payment progress or Paid */}
          {(() => {
            const amountPaid = p.supplier_amount_paid || 0;
            const totalCost = p.actual_cost || p.estimated_cost || 0;
            const isFullyPaid = p.supplier_paid && (amountPaid >= totalCost || totalCost === 0);
            const hasPartialPayment = amountPaid > 0 && amountPaid < totalCost;
            // Sticky-paid + cost raised: show green "Paid" + tiny yellow unpaid-balance chip.
            // Common case: material was paid in full, then a change order bumped the cost.
            // Only show the chip when actual payments have been logged (amountPaid > 0).
            // If supplier_paid=true but no payment ledger exists, trust the flag — the
            // user marked it paid manually and we don't have line-item detail.
            const ROUND_THRESHOLD = 1;  // ignore sub-dollar rounding gaps
            const unpaidBalance =
              p.supplier_paid && amountPaid > 0 && (totalCost - amountPaid) > ROUND_THRESHOLD
                ? (totalCost - amountPaid)
                : 0;
            
            if (isFullyPaid) {
              return <Badge className="bg-green-100 text-green-700 text-xs">Paid</Badge>;
            }
            
            if (hasPartialPayment) {
              const percentage = Math.round((amountPaid / totalCost) * 100);
              return (
                <Badge className="bg-blue-100 text-blue-700 text-xs flex items-center gap-1">
                  <span>${amountPaid.toLocaleString()}</span>
                  <span className="text-blue-400">/</span>
                  <span>${totalCost.toLocaleString()}</span>
                  <span className="text-blue-500 font-medium">({percentage}%)</span>
                </Badge>
              );
            }
            
            // If marked as paid but cost raised AFTER payment logging, show Paid + unpaid-balance reminder
            if (p.supplier_paid && unpaidBalance > 0) {
              return (
                <>
                  <Badge className="bg-green-100 text-green-700 text-xs">Paid</Badge>
                  <Badge
                    className="bg-amber-100 text-amber-700 border border-amber-300 text-xs"
                    data-testid={`unpaid-balance-${p.id}`}
                    title="Actual cost was raised after this phase was marked paid. Click Pay More to log the additional payment when ready."
                  >
                    +${unpaidBalance.toLocaleString()} unpaid
                  </Badge>
                </>
              );
            }
            
            // If marked as paid but no amount tracked, still show Paid
            if (p.supplier_paid) {
              return <Badge className="bg-green-100 text-green-700 text-xs">Paid</Badge>;
            }
            
            return null;
          })()}
          {p.status && p.status !== 'pending' && (
            <Badge variant="outline" className={`text-xs ${p.status === 'completed' ? 'bg-green-50 text-green-600' : p.status === 'in_progress' ? 'bg-blue-50 text-blue-600' : ''}`}>
              {p.status}
            </Badge>
          )}
        </div>
        {/* v2.9.14: Docs button ALWAYS renders on every item (paid or not,
            with or without a contractor). Only the Pay button stays gated. */}
        <div className="mt-2 flex items-center justify-end gap-1.5 flex-wrap">
          {renderDocsBtn(p)}
          {renderQuickDrawBtn(p)}
          {!isReadOnly && onPayContractor && (() => {
            const totalCost = p.actual_cost || p.estimated_cost || 0;
            const amountPaid = p.supplier_amount_paid || 0;
            const stillOwes = amountPaid < totalCost;
            const isFullyPaid = p.supplier_paid && !stillOwes;
            if (isFullyPaid) {
              return (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs border-amber-500 text-amber-700 hover:bg-amber-50"
                  onClick={(e) => { e.stopPropagation(); handleQuickUnmarkPaid(p); }}
                  data-testid={`reopen-paid-${p.id}`}
                  title="Reopen this paid phase so you can request a lien waiver, pay more, or make corrections"
                >
                  <RotateCcw className="w-3 h-3 mr-1" /> Reopen
                </Button>
              );
            }
            const c = contractors.find(c => c.id === p.contractor_id) || null;
            const remaining = Math.max(totalCost - amountPaid, 0);
            const partial = amountPaid > 0;
            const label = partial
              ? (useBatchB ? `Pay More · $${remaining.toLocaleString(undefined, { maximumFractionDigits: 0 })} left` : 'Pay More')
              : 'Pay';
            const tone = partial ? 'border-amber-500 text-amber-700 hover:bg-amber-50' : 'border-green-500 text-green-600 hover:bg-green-50';
            return (
              <Button
                size="sm"
                variant="outline"
                className={`h-7 text-xs ${tone}`}
                onClick={(e) => { e.stopPropagation(); onPayContractor(p, c); }}
                data-testid={`morph-btn-${p.id}`}
                data-morph-state={partial ? 'partial' : 'unpaid'}
              >
                <CreditCard className="w-3 h-3 mr-1" /> {label}
              </Button>
            );
          })()}
        </div>
        {/* v2.5.4 (Batch B): Start / Complete / Edit inline row for materials */}
        {useBatchB && !isReadOnly && (
          <div className="flex items-center justify-end gap-2 mt-2 pt-2 border-t border-slate-100">
            {p.status !== 'in_progress' && p.status !== 'completed' && (
              <button
                onClick={(e) => { e.stopPropagation(); openStartDialog(p); }}
                className="text-xs text-slate-600 hover:text-emerald-700 inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
                data-testid={`inline-start-${p.id}`}
              >
                <Play className="w-3 h-3" /> Start
              </button>
            )}
            {p.status === 'in_progress' && (
              <button
                onClick={(e) => { e.stopPropagation(); openCompleteDialog(p); }}
                className="text-xs text-slate-600 hover:text-emerald-700 inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
                data-testid={`inline-complete-${p.id}`}
              >
                <CheckCircle2 className="w-3 h-3" /> Complete
              </button>
            )}
            {(p.status === 'in_progress' || p.status === 'completed') && (
              <button
                onClick={(e) => { e.stopPropagation(); handleReopenPhase(p); }}
                className="text-xs text-slate-500 hover:text-amber-700 inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
                data-testid={`inline-reopen-${p.id}`}
              >
                <RotateCcw className="w-3 h-3" /> {p.status === 'completed' ? 'Reopen' : 'Undo'}
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); openFullEditDialog(p); }}
              className="text-xs text-slate-600 hover:text-blue-700 inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
              data-testid={`inline-edit-${p.id}`}
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
          </div>
        )}
      </div>
      </React.Fragment>
    );
  };

  // Render Labor Item Card
  const renderLaborItem = (p, cpPhaseNumber) => {
    const contractor = contractors.find(c => c.id === p.contractor_id);
    // v2.4: inline quick-edit card removed — clicking opens Full Edit drawer.
    const isEditing = false;
    
    if (isEditing) {
      return (
        <div key={p.id} className="bg-white rounded-lg p-3 shadow-sm border-2 border-blue-400">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-sm">{p.name}</span>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => saveInlineEdit(p.id)}>
                <Save className="w-4 h-4 text-green-600" />
              </Button>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={cancelInlineEdit}>
                <X className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <Label className="text-xs">Est. Cost</Label>
              <Input
                type="number"
                className="h-7 text-xs"
                value={inlineEditData.estimated_cost}
                onChange={(e) => setInlineEditData({...inlineEditData, estimated_cost: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-xs">Actual Cost</Label>
              <Input
                type="number"
                className="h-7 text-xs"
                value={inlineEditData.actual_cost}
                onChange={(e) => setInlineEditData({...inlineEditData, actual_cost: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-xs">Start Date</Label>
              <Input
                type="date"
                className="h-7 text-xs"
                value={inlineEditData.start_date}
                onChange={(e) => setInlineEditData({...inlineEditData, start_date: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-xs">End Date</Label>
              <Input
                type="date"
                className="h-7 text-xs"
                value={inlineEditData.end_date}
                onChange={(e) => setInlineEditData({...inlineEditData, end_date: e.target.value})}
              />
            </div>
          </div>
          <div className="mt-2">
            <Label className="text-xs">Status</Label>
            <Select value={inlineEditData.status} onValueChange={(v) => setInlineEditData({...inlineEditData, status: v})}>
              <SelectTrigger className="h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    }

    return (
      <React.Fragment key={p.id}>
      <div 
        id={`phase-row-${p.id}`}
        data-phase-id={p.id}
        className="bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
        onClick={() => openFullEditDialog(p)}
      >
        <div className="flex items-center justify-between">
          <span className="font-medium text-sm truncate flex-1">{p.name}</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openFullEditDialog(p); }}>
                <Pencil className="w-4 h-4 mr-2" /> Full Edit
              </DropdownMenuItem>
              {p.status !== 'in_progress' && p.status !== 'completed' && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openStartDialog(p); }}>
                  <Play className="w-4 h-4 mr-2" /> Start
                </DropdownMenuItem>
              )}
              {p.status === 'in_progress' && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openCompleteDialog(p); }}>
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Mark Complete
                </DropdownMenuItem>
              )}
              {(p.status === 'in_progress' || p.status === 'completed') && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleReopenPhase(p); }}>
                  <RotateCcw className="w-4 h-4 mr-2" /> {p.status === 'completed' ? 'Reopen (undo complete)' : 'Undo start'}
                </DropdownMenuItem>
              )}
              {/* v2.4: "Mark as Paid" / "Unmark as Paid" removed — use the
                  Pay button which opens the new Log Payment drawer instead.
                  v2.5.3: "View Documents" removed — fully-paid phases expose
                  a dedicated "View Docs" button next to the Paid badge. */}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleUnlinkPhase(cpPhaseNumber, p.id); }}>
                <X className="w-4 h-4 mr-2" /> Unlink
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={(e) => { e.stopPropagation(); handleDeletePhase(p.id); }}
                className="text-red-600"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center justify-between mt-1 text-xs text-slate-500">
          <span>Est: ${(p.estimated_cost || 0).toLocaleString()}</span>
          <Badge variant="outline" className={`text-xs ${
            p.status === 'completed' ? 'bg-green-50 text-green-600' : 
            p.status === 'in_progress' ? 'bg-blue-50 text-blue-600' : ''
          }`}>
            {p.status || 'pending'}
          </Badge>
        </div>
        {(project?.units || project?.sqft) && (p.estimated_cost > 0) ? (
          <div className="text-[10px] text-slate-400 mt-0.5" data-testid={`phase-item-permetrics-${p.id}`}>
            {project?.units ? `$${Math.round((p.estimated_cost || 0) / project.units).toLocaleString()}/unit` : ''}
            {project?.units && project?.sqft ? ' · ' : ''}
            {project?.sqft ? `$${((p.estimated_cost || 0) / project.sqft).toFixed(2)}/SF` : ''}
          </div>
        ) : null}
        {contractor && (
          <p className="text-xs text-slate-500 mt-1">
            <User className="w-3 h-3 inline mr-1" />
            {contractor.company || contractor.name}
          </p>
        )}
        {/* Bank Category Badge */}
        {p.bank_category && (
          <div className="mt-1">
            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
              {getBankCategoryLabel(p.bank_category)}
            </Badge>
          </div>
        )}
        {p.start_date && (
          <div className="text-xs text-slate-400 mt-1">
            <Calendar className="w-3 h-3 inline mr-1" />
            {formatDate(p.start_date)}
            {p.end_date && ` - ${formatDate(p.end_date)}`}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            {/* Payment Status Badge - Sticky-paid: stays Green even if cost was
                later raised. Yellow chip only shown when actual payment records
                exist (amountPaid > 0) AND a gap > $1 — avoids false-positive
                chips on legacy auto-paid phases with no ledger entries. */}
            {p.is_paid && (() => {
              const amountPaid = p.supplier_amount_paid || 0;
              const totalCost = p.actual_cost || p.estimated_cost || 0;
              const ROUND_THRESHOLD = 1;
              const unpaidBalance =
                amountPaid > 0 && (totalCost - amountPaid) > ROUND_THRESHOLD
                  ? (totalCost - amountPaid)
                  : 0;
              return (
                <>
                  <Badge className="bg-green-100 text-green-700 text-xs">Paid</Badge>
                  {unpaidBalance > 0 && (
                    <Badge
                      className="bg-amber-100 text-amber-700 border border-amber-300 text-xs"
                      data-testid={`unpaid-balance-${p.id}`}
                      title="Actual cost was raised after this phase was marked paid. Click Pay More to log the additional payment when ready."
                    >
                      +${unpaidBalance.toLocaleString()} unpaid
                    </Badge>
                  )}
                </>
              );
            })()}
            {p.is_paid && p.is_legacy_bypass && (
              <Badge className="bg-amber-100 text-amber-700 text-xs border border-amber-200">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Legacy Bypass
              </Badge>
            )}
            {/* v2.5.6: Inspection Gate badge + owner toggle */}
            {inspectionGateEnabled && !p.is_prepaid && contractor && (() => {
              const requires = !!p.requires_inspection;
              const satisfied = isInspectionSatisfied(p);
              const items = Array.isArray(p.inspections) ? p.inspections : [];
              const passedCount = items.filter(i => i?.status === 'passed').length
                + ((p.inspection_file_id || p.inspection_passed) && !items.some(i => i?.status === 'passed') ? 1 : 0);
              const totalCount = Math.max(items.length, (p.inspection_file_id ? 1 : 0));
              if (!requires) {
                // Off — owner can flip it ON inline
                return (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleRequiresInspection(p); }}
                    className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-500 hover:border-blue-300 hover:text-blue-700 hover:bg-blue-50"
                    data-testid={`require-inspection-toggle-off-${p.id}`}
                    title="Require an inspection report from the contractor"
                  >
                    <ShieldAlert className="w-3 h-3 opacity-60" />
                    + Require inspection
                  </button>
                );
              }
              // On — badge with state + a small "off" icon to flip back
              return (
                <span className="inline-flex items-center" data-testid={`inspection-pill-${p.id}`}>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openPhaseInspections(p); }}
                    className={`inline-flex items-center gap-1 text-[11px] pl-1.5 pr-2 py-0.5 rounded-l border border-r-0 ${
                      satisfied
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                        : 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100'
                    }`}
                    data-testid={`inspection-gate-badge-${p.id}`}
                    title={satisfied
                      ? 'Inspection report received — click to view.'
                      : 'Awaiting inspection report from contractor — click to manage'}
                  >
                    {satisfied ? <ShieldCheck className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
                    {satisfied
                      ? `Inspection ✓${totalCount > 1 ? ` ${passedCount}/${totalCount}` : ''}`
                      : `Inspection ${passedCount}/${Math.max(totalCount, 1)}`}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(`Stop requiring an inspection report on "${p.name}"?\n\nThe contractor will no longer be asked for one in the compliance email. You can re-enable any time.`)) {
                        toggleRequiresInspection(p);
                      }
                    }}
                    className={`inline-flex items-center text-[11px] px-1 py-0.5 rounded-r border ${
                      satisfied
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                        : 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100'
                    } hover:text-red-600`}
                    data-testid={`require-inspection-toggle-on-${p.id}`}
                    title="Turn off inspection requirement for this phase"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })()}
          </div>
          {/* v2.9.14: Docs + Draw always render; Pay stays conditional. */}
          <div className="flex items-center gap-2 flex-wrap justify-end min-w-0">
          {renderDocsBtn(p)}
          {renderQuickDrawBtn(p)}
          {!isReadOnly && contractor && onPayContractor && (() => {
            const totalCost = p.actual_cost || p.estimated_cost || 0;
            const amountPaid = p.supplier_amount_paid || 0;
            const stillOwes = amountPaid < totalCost;
            const isFullyPaid = p.is_paid && !stillOwes;
            if (isFullyPaid) {
              return (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs border-amber-500 text-amber-700 hover:bg-amber-50"
                  onClick={(e) => { e.stopPropagation(); handleQuickUnmarkPaid(p); }}
                  data-testid={`reopen-paid-${p.id}`}
                  title="Reopen this paid phase so you can request a lien waiver, pay more, or make corrections"
                >
                  <RotateCcw className="w-3 h-3 mr-1" /> Reopen
                </Button>
              );
            }
            const remaining = Math.max(totalCost - amountPaid, 0);
            const partial = amountPaid > 0;
            const label = partial
              ? (useBatchB ? `Pay More · $${remaining.toLocaleString(undefined, { maximumFractionDigits: 0 })} left` : 'Pay More')
              : 'Pay';
            const tone = partial ? 'border-amber-500 text-amber-700 hover:bg-amber-50' : 'border-green-500 text-green-600 hover:bg-green-50';
            return (
              <Button
                size="sm"
                variant="outline"
                className={`h-7 text-xs ${tone}`}
                onClick={(e) => { e.stopPropagation(); onPayContractor(p, contractor); }}
                data-testid={`morph-btn-${p.id}`}
                data-morph-state={partial ? 'partial' : 'unpaid'}
              >
                <CreditCard className="w-3 h-3 mr-1" /> {label}
              </Button>
            );
          })()}
          </div>
        </div>
        {/* v2.5.4 (Batch B): Start / Complete / Edit inline row */}
        {useBatchB && !isReadOnly && (
          <div className="flex items-center justify-end gap-2 mt-2 pt-2 border-t border-slate-100">
            {p.status !== 'in_progress' && p.status !== 'completed' && (
              <button
                onClick={(e) => { e.stopPropagation(); openStartDialog(p); }}
                className="text-xs text-slate-600 hover:text-emerald-700 inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
                data-testid={`inline-start-${p.id}`}
              >
                <Play className="w-3 h-3" /> Start
              </button>
            )}
            {p.status === 'in_progress' && (
              <button
                onClick={(e) => { e.stopPropagation(); openCompleteDialog(p); }}
                className="text-xs text-slate-600 hover:text-emerald-700 inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
                data-testid={`inline-complete-${p.id}`}
              >
                <CheckCircle2 className="w-3 h-3" /> Complete
              </button>
            )}
            {(p.status === 'in_progress' || p.status === 'completed') && (
              <button
                onClick={(e) => { e.stopPropagation(); handleReopenPhase(p); }}
                className="text-xs text-slate-500 hover:text-amber-700 inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
                data-testid={`inline-reopen-${p.id}`}
              >
                <RotateCcw className="w-3 h-3" /> {p.status === 'completed' ? 'Reopen' : 'Undo'}
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); openFullEditDialog(p); }}
              className="text-xs text-slate-600 hover:text-blue-700 inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
              data-testid={`inline-edit-${p.id}`}
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
          </div>
        )}
      </div>
    </React.Fragment>
    );
  };

  if (loading) {
    return (
      <Card className="bg-white rounded-xl shadow-sm">
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500">Loading construction phases...</p>
        </CardContent>
      </Card>
    );
  }

  const timelineProgress = getTimelineProgress();
  const unassignedPhases = getUnassignedPhases();

  return (
    <div className="space-y-6" data-testid="construction-phases-v2">
      {/* Project Header with Timeline */}
      <div className="flex items-start justify-between bg-white rounded-xl p-4 shadow-sm border border-slate-200">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{project?.name || 'Project'}</h1>
          <p className="text-slate-500">{project?.address}</p>
          {/* Timeline dates */}
          <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
            {project?.start_date && (
              <span className="flex items-center gap-1">
                <Play className="w-4 h-4 text-blue-500" />
                Start: {formatDateWithTimezone(project.start_date, project?.timezone)}
              </span>
            )}
            {project?.projected_completion_date && (
              <span className="flex items-center gap-1">
                <Flag className="w-4 h-4 text-green-500" />
                Target: {formatDateWithTimezone(project.projected_completion_date, project?.timezone)}
              </span>
            )}
          </div>
        </div>
        {/* New Draw Request Button */}
        {onRequestDraw && (
          <Button
            onClick={onRequestDraw}
            className="text-white hover:opacity-90"
            style={{ backgroundColor: '#1E4ED8' }}
            data-testid="request-draw-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Draw Request
          </Button>
        )}
      </div>

      {/* Visual Timeline Bar - Solid colors, more visible */}
      {project?.start_date && (
        <Card className="bg-slate-800 rounded-xl border border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between text-sm text-white mb-2">
              <span className="font-medium">Project Timeline</span>
              <span className="font-bold">{Math.round(timelineProgress)}% elapsed</span>
            </div>
            <div className="relative h-6 bg-slate-600 rounded-lg overflow-hidden">
              {/* Progress fill */}
              <div 
                className="absolute inset-y-0 left-0 bg-blue-500 transition-all"
                style={{ width: `${timelineProgress}%` }}
              />
              {/* Current date marker */}
              <div 
                className="absolute top-0 bottom-0 w-1 bg-white shadow-lg"
                style={{ left: `${Math.min(99, timelineProgress)}%` }}
              />
              {/* Percentage labels */}
              <div className="absolute inset-0 flex items-center justify-between px-3 text-xs font-medium">
                <span className="text-white">0%</span>
                <span className="text-white">50%</span>
                <span className="text-white">100%</span>
              </div>
            </div>
            <div className="flex justify-between text-xs text-slate-400 mt-2">
              <span>{project.start_date ? formatDateWithTimezone(project.start_date, project?.timezone) : 'Start'}</span>
              <span className="text-blue-400 font-medium">Today</span>
              <span>{project.projected_completion_date ? formatDateWithTimezone(project.projected_completion_date, project?.timezone) : 'End'}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Overall Progress & Financial Summary */}
      <Card className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl shadow-lg border-0">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Progress */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-white text-lg font-semibold">Overall Progress</h2>
                <span className="text-3xl font-bold text-white">{overallProgress}%</span>
              </div>
              <div className="h-4 bg-slate-600 rounded-full overflow-hidden mb-4">
                <div 
                  className={`h-full transition-all duration-500 ${overallProgress >= 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
              <div className="flex items-center gap-4 text-sm text-slate-300">
                <span className="flex items-center gap-1">
                  <Circle className="w-3 h-3 fill-slate-400" /> Not Started
                </span>
                <span className="flex items-center gap-1">
                  <Circle className="w-3 h-3 fill-blue-500" /> In Progress
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 fill-green-500" /> Complete
                </span>
              </div>
              {/* v2.6 — prominent Request COI CTA (moved out of the ⋮ menu) */}
              {onRequestCOI && (
                <button
                  onClick={onRequestCOI}
                  data-testid="request-coi-cta"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-white/95 hover:bg-white text-blue-700 font-semibold text-sm px-4 py-2.5 shadow-sm ring-1 ring-white/40 transition-colors"
                >
                  <ShieldCheck className="w-4 h-4" />
                  Request Compliance Docs
                </button>
              )}
              {/* Per-line Quick Draw — controlled instance opened from a line item's "Draw" button */}
              {!isReadOnly && (
                <QuickDrawModal
                  projectId={projectId}
                  userId={userId}
                  phases={budgetPhases}
                  preselectId={quickDrawPhaseId}
                  hideTrigger
                  open={!!quickDrawPhaseId}
                  onOpenChange={(o) => { if (!o) setQuickDrawPhaseId(null); }}
                  onComplete={() => { if (typeof loadData === 'function') loadData(); }}
                />
              )}
            </div>
            
            {/* Right: Financial Summary */}
            <div className="space-y-3">
              {/* Financial summary consolidated into the All-In Project Cost card below (v2.6.1) */}
              {/* Row 2: Acquisition Cost & Closing Costs */}
              {(() => {
                const acquisitionCost = (project?.purchase_price_home || 0) + (project?.purchase_price_land || 0);
                const closingCosts = project?.acquisition_closing_costs || 0;
                // v2.6.0 — All-In Project Cost now leads with the projected
                // TOTAL (estimated construction + property), checked against
                // the declared budget, with the budget "drawing down" as money
                // is actually paid. Previously it led with actual-spent, which
                // looked far smaller than the sum of phase estimates and
                // confused owners on projects that hadn't broken ground yet.
                const propertyCost = acquisitionCost + closingCosts;
                // v2.9.10 — All-In now BLENDS in real costs: for any line with a
                // logged Actual cost, use that real number (net of material
                // refunds); lines with no actual yet fall back to the Estimate.
                // So logging real spend moves the All-In total and the over/under.
                const totalProjected = budgetPhases.reduce((s, p) => {
                  const est = parseFloat(p.estimated_cost) || 0;
                  const actualNet = (parseFloat(p.actual_cost) || 0) - (p.phase_type === 'material' ? (p.return_refund_amount || 0) : 0);
                  return s + (actualNet > 0 ? actualNet : est);
                }, 0);
                const allInEstimated = totalProjected + propertyCost;   // full projected cost (actuals blended in)
                const declaredBudget = totalBudget;                     // budget to compare against
                const spentToDate = totalPaid;                          // construction paid out
                const budgetUsed = propertyCost + spentToDate;          // money out of the budget so far
                const availableBudget = declaredBudget - budgetUsed;    // what's left to spend
                // v2.9.13 — the headline bar tracks money DRAWN from the loan
                // against the declared budget (matches the budget pulse pings and
                // the "$X drawn" figure below), NOT logged spend. Builders think
                // in draws, so 33% drawn should read 33% here.
                const budgetDrawnPct = declaredBudget > 0 ? Math.min(100, Math.max(0, ((project?.amount_drawn || 0) / declaredBudget) * 100)) : 0;
                const underBudget = allInEstimated <= declaredBudget;
                const budgetVariance = Math.abs(declaredBudget - allInEstimated);
                const hasAcquisitionCosts = acquisitionCost > 0 || closingCosts > 0;

                // contingency / draw remaining / cost-per-sqft — folded into the card (v2.6.1)
                const contingencyBudget = project?.contingency_budget || 0;
                const constructionBudgetForContingency = project?.construction_budget || project?.total_budget || 0;
                const contingencyOver = totalActual > constructionBudgetForContingency && constructionBudgetForContingency > 0;
                const contingencyUsed = Math.min(contingencyOver ? totalActual - constructionBudgetForContingency : 0, contingencyBudget);
                const contingencyRemaining = contingencyBudget - contingencyUsed;
                const drawRemaining = project?.loan_available_for_draw || Math.max(0, (project?.loan_principal_amount || 0) - (project?.amount_drawn || 0));
                const amountDrawn = project?.amount_drawn || 0;
                const sqft = parseFloat(project?.sqft) || 0;
                const buildPhasesSqft = (budgetPhases || []).filter(p => !p.exclude_from_sqft);
                const buildEstimatedSqft = buildPhasesSqft.reduce((s, p) => s + (parseFloat(p.estimated_cost) || 0), 0);
                const buildActualSqft = buildPhasesSqft.reduce((s, p) => s + (parseFloat(p.actual_cost) || 0) - (p.phase_type === 'material' ? (p.return_refund_amount || 0) : 0), 0);
                const estPerSqft = sqft > 0 ? buildEstimatedSqft / sqft : 0;
                const actPerSqft = sqft > 0 ? buildActualSqft / sqft : 0;
                const sqftVariance = estPerSqft > 0 ? ((actPerSqft - estPerSqft) / estPerSqft) * 100 : 0;
                const sqftUnder = sqftVariance < 0;
                void hasAcquisitionCosts;

                return (
                  <>
                    {/* All-In Project Cost — consolidated financial summary (v2.6.1) */}
                    <div className="bg-gradient-to-r from-blue-600/40 to-cyan-600/40 rounded-xl p-4 border border-blue-500/30" data-testid="all-in-project-cost-card">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-1 mb-1">
                            <p className="text-cyan-200 text-xs font-medium">All-In Project Cost</p>
                            <Popover>
                              <PopoverTrigger asChild>
                                <button className="hover:bg-cyan-500/30 rounded-full p-0.5 transition-colors">
                                  <Info className="w-3 h-3 text-cyan-300 hover:text-cyan-100" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent side="top" className="max-w-xs bg-slate-800 text-white p-3 text-xs border-slate-600">
                                <p className="font-semibold mb-1">Projected total project cost</p>
                                <p>The full projected cost to complete this project:</p>
                                <ul className="list-disc ml-4 mt-1 space-y-0.5">
                                  <li>Phase costs — your logged <b>Actual</b> where entered, otherwise the Estimate</li>
                                  <li>Property — land + closing</li>
                                </ul>
                                <p className="mt-1">Compared to your declared budget. As you log real costs, this total and the over/under update automatically.</p>
                              </PopoverContent>
                            </Popover>
                          </div>
                          <p className="text-2xl font-bold text-white" data-testid="all-in-estimated-value">${allInEstimated.toLocaleString()}</p>
                          <p className="text-[11px] text-cyan-200 mt-0.5">Projected total · actuals where logged + estimates + property</p>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-1"><p className="text-cyan-300 text-[11px]">Budget</p><MetricInfo title="Budget">Your declared total budget for the project (loan + equity). The All-In estimated cost is compared against this to show whether you are over or under.</MetricInfo></div>
                          <p className="text-base font-bold text-white">${declaredBudget.toLocaleString()}</p>
                          <span className={`inline-flex items-center gap-1 mt-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${underBudget ? 'bg-emerald-500/25 text-emerald-300' : 'bg-red-500/25 text-red-300'}`} data-testid="budget-variance-chip">
                            ${budgetVariance.toLocaleString()} {underBudget ? 'under' : 'over'}
                          </span>
                        </div>
                      </div>
                      {/* dwindling budget bar */}
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-[11px] text-cyan-200 mb-1">
                          <span>Budget drawn</span>
                          <span>{budgetDrawnPct.toFixed(1)}%</span>
                        </div>
                        <div className="h-2.5 rounded-full bg-white/15 overflow-hidden">
                          <div className="h-2.5 rounded-full" style={{ width: `${budgetDrawnPct}%`, background: '#5c9e33' }} data-testid="budget-used-bar" />
                        </div>
                      </div>
                      {/* spent / property / available */}
                      <div className="grid grid-cols-3 gap-2 mt-3">
                        <div className="bg-white/10 rounded-lg p-2.5">
                          <div className="flex items-center gap-1"><p className="text-[10px] text-cyan-200 uppercase tracking-wide">Spent</p><MetricInfo title="Spent to date">Construction cash actually paid out to contractors and vendors so far. The property purchase is tracked separately.</MetricInfo></div>
                          <p className="text-sm font-bold text-white mt-0.5" data-testid="all-in-spent-value">${spentToDate.toLocaleString()}</p>
                          <p className="text-[10px] text-cyan-300/80">construction</p>
                        </div>
                        <div className="bg-white/10 rounded-lg p-2.5">
                          <div className="flex items-center gap-1"><p className="text-[10px] text-cyan-200 uppercase tracking-wide">Property</p><MetricInfo title="Property (Land + Closing)">Money already paid to buy the land/home plus closing costs. A real project cost, shown separately from construction spend.</MetricInfo></div>
                          <p className="text-sm font-bold text-white mt-0.5">${propertyCost.toLocaleString()}</p>
                          <p className="text-[10px] text-cyan-300/80">land + closing</p>
                        </div>
                        <div className="bg-emerald-500/20 rounded-lg p-2.5 border border-emerald-400/30">
                          <div className="flex items-center gap-1"><p className="text-[10px] text-emerald-200 uppercase tracking-wide">Available</p><MetricInfo title="Available">Money still available to spend: Budget minus Property minus Spent. It dwindles as you log payments, so you always see what is still available.</MetricInfo></div>
                          <p className="text-sm font-bold text-emerald-300 mt-0.5" data-testid="all-in-available-value">${availableBudget.toLocaleString()}</p>
                          <p className="text-[10px] text-emerald-300/80">left to spend</p>
                        </div>
                      </div>
                      {/* contingency / draw remaining / cost per sqft */}
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        <div className={`rounded-lg p-2.5 ${contingencyOver && contingencyBudget > 0 ? 'bg-red-500/20 border border-red-400/30' : 'bg-white/10'}`}>
                          <div className="flex items-center gap-1"><p className="text-[10px] text-cyan-200 uppercase tracking-wide">Contingency</p><MetricInfo title="Contingency">Reserve buffer remaining. It is drawn down automatically when construction costs run over your construction budget.</MetricInfo></div>
                          <p className="text-sm font-bold text-white mt-0.5" data-testid="all-in-contingency-value">${contingencyRemaining.toLocaleString()}</p>
                          <p className="text-[10px] text-cyan-300/80">{contingencyUsed > 0 ? `$${contingencyUsed.toLocaleString()} used` : 'remaining'}</p>
                        </div>
                        <div className="bg-white/10 rounded-lg p-2.5">
                          <div className="flex items-center gap-1"><p className="text-[10px] text-cyan-200 uppercase tracking-wide">Draw Remaining</p><MetricInfo title="Draw Remaining">How much of your construction loan you can still draw from the bank. The Drawn figure is the amount already pulled.</MetricInfo></div>
                          <p className="text-sm font-bold text-white mt-0.5" data-testid="all-in-draw-remaining-value">${drawRemaining.toLocaleString()}</p>
                          <p className="text-[10px] text-cyan-300/80">{amountDrawn > 0 ? `$${amountDrawn.toLocaleString()} drawn` : 'available'}</p>
                        </div>
                        <div className="bg-white/10 rounded-lg p-2.5">
                          <div className="flex items-center gap-1"><p className="text-[10px] text-cyan-200 uppercase tracking-wide">Cost / sqft{sqft > 0 ? ` · ${sqft.toLocaleString()}` : ''}</p><MetricInfo title="Cost per square foot">Actual build cost divided by square footage (permits/fees excluded). Est shows your estimated cost per sqft, with the variance versus actual.</MetricInfo></div>
                          {sqft > 0 ? (
                            <>
                              <p className="text-sm font-bold text-white mt-0.5 tabular-nums" data-testid="all-in-cost-per-sqft-value">${actPerSqft.toFixed(2)}</p>
                              <p className="text-[10px] text-cyan-300/80 tabular-nums">Est ${estPerSqft.toFixed(2)}{estPerSqft > 0 && <span className={sqftUnder ? 'text-emerald-300 ml-1' : 'text-red-300 ml-1'}>{sqftUnder ? '▼' : '▲'} {Math.abs(sqftVariance).toFixed(1)}%</span>}</p>
                            </>
                          ) : (
                            <p className="text-[10px] text-cyan-300/80 mt-0.5">Add sqft in Edit Project</p>
                          )}
                        </div>
                      </div>
                      {/* v2.6 — peer cost benchmarks folded into the All-In card, red-accented to stand out */}
                      {benchmark && benchmark.project && (benchmark.project.per_unit != null || benchmark.project.per_sf != null) && (
                        <div className="mt-2 rounded-lg bg-red-500/15 border border-red-400/40 p-2.5" data-testid="all-in-benchmark-band">
                          <div className="flex items-center gap-1 mb-1.5">
                            <BarChart3 className="w-3 h-3 text-red-300" />
                            <p className="text-[10px] text-red-200 uppercase tracking-wide font-semibold">Cost benchmarks vs your portfolio</p>
                            <MetricInfo title="Cost benchmarks">This project&apos;s all-in cost per unit and per square foot compared to the average across your other projects. Red means above your average.</MetricInfo>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {benchmark.project.per_unit != null && (
                              <div data-testid="all-in-benchmark-per-unit">
                                <p className="text-sm font-bold text-white tabular-nums">${Math.round(benchmark.project.per_unit).toLocaleString()}<span className="text-[10px] font-normal text-cyan-200 ml-1">/ unit</span></p>
                                <p className="text-[10px] tabular-nums">
                                  {benchmark.delta_pct?.per_unit != null && (
                                    <span className={benchmark.delta_pct.per_unit > 0.5 ? 'text-red-300 font-semibold' : benchmark.delta_pct.per_unit < -0.5 ? 'text-emerald-300 font-semibold' : 'text-cyan-300'}>
                                      {benchmark.delta_pct.per_unit > 0 ? '▲ +' : benchmark.delta_pct.per_unit < 0 ? '▼ ' : ''}{benchmark.delta_pct.per_unit}% vs avg
                                    </span>
                                  )}
                                  {benchmark.portfolio?.per_unit_avg != null && <span className="text-cyan-300/70 ml-1">avg ${Math.round(benchmark.portfolio.per_unit_avg).toLocaleString()}</span>}
                                </p>
                              </div>
                            )}
                            {benchmark.project.per_sf != null && (
                              <div data-testid="all-in-benchmark-per-sf">
                                <p className="text-sm font-bold text-white tabular-nums">${Number(benchmark.project.per_sf).toFixed(2)}<span className="text-[10px] font-normal text-cyan-200 ml-1">/ SF</span></p>
                                <p className="text-[10px] tabular-nums">
                                  {benchmark.delta_pct?.per_sf != null && (
                                    <span className={benchmark.delta_pct.per_sf > 0.5 ? 'text-red-300 font-semibold' : benchmark.delta_pct.per_sf < -0.5 ? 'text-emerald-300 font-semibold' : 'text-cyan-300'}>
                                      {benchmark.delta_pct.per_sf > 0 ? '▲ +' : benchmark.delta_pct.per_sf < 0 ? '▼ ' : ''}{benchmark.delta_pct.per_sf}% vs avg
                                    </span>
                                  )}
                                  {benchmark.portfolio?.per_sf_avg != null && <span className="text-cyan-300/70 ml-1">avg ${Number(benchmark.portfolio.per_sf_avg).toFixed(2)}</span>}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
          
          {/* Auto-Assign Button + Reconcile Payment Status */}
          <div className="mt-4 pt-4 border-t border-slate-600 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={handleAutoAssign}
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                data-testid="auto-assign-btn"
              >
                <Wand2 className="w-4 h-4 mr-2" />
                Auto-Assign Unlinked Items ({unassignedPhases.length})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const res = await apiService.reconcilePhasePayments(projectId, project?.user_id);
                    const d = res?.data || {};
                    if (d.phases_healed > 0) {
                      toast.success(
                        `Fixed ${d.phases_healed} stuck phase${d.phases_healed === 1 ? '' : 's'} — Total Paid should jump.`
                      );
                      if (typeof onRefresh === 'function') onRefresh();
                      window.location.reload();
                    } else {
                      toast.info(`All ${d.phases_already_paid || 0} paid phases are already in sync.`);
                    }
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Reconcile failed');
                  }
                }}
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                data-testid="reconcile-payments-btn"
                title="Scan all phases and finalize any that were fully paid but stuck without the 'paid' badge"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Sync payment status
              </Button>
            </div>
            {unassignedPhases.length > 0 && (
              <button
                onClick={() => setShowUnassigned(!showUnassigned)}
                className="text-amber-400 text-sm hover:text-amber-300 flex items-center gap-1"
              >
                <AlertCircle className="w-4 h-4" />
                {unassignedPhases.length} items not assigned
                <ChevronDown className={`w-4 h-4 transition-transform ${showUnassigned ? 'rotate-180' : ''}`} />
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Holding Costs Card - Separate from 8-phase progress */}
      <HoldingCostsCard projectId={projectId} project={project} isReadOnly={isReadOnly} />

      {/* Unassigned Items Section */}
      {unassignedPhases.length > 0 && showUnassigned && (
        <Card className="bg-amber-50 border-amber-200 rounded-xl">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Unassigned Items ({unassignedPhases.length})
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowUnassigned(false)}
                className="text-amber-600 hover:text-amber-800"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-sm text-amber-700 mb-3">
              These items are not linked to any construction phase. Assign them to track progress properly.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {unassignedPhases.map(p => (
                <div key={p.id} className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm truncate flex-1">{p.name}</span>
                    <Badge variant="outline" className="text-xs">
                      {p.phase_type || 'general'}
                    </Badge>
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`Delete "${p.name}"? This removes the line item from the project.`)) {
                          handleDeletePhase(p.id, true);
                        }
                      }}
                      className="text-slate-400 hover:text-red-600 transition-colors"
                      title="Delete this item"
                      data-testid={`unassigned-delete-${p.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Est: ${(p.estimated_cost || 0).toLocaleString()}
                    {p.bank_category && (
                      <span className="ml-2 text-blue-600">{getBankCategoryLabel(p.bank_category)}</span>
                    )}
                  </div>
                  <div className="mt-2">
                    <Select
                      value=""
                      onValueChange={(value) => {
                        if (value === 'holding_costs') {
                          handleAssignToHoldingCosts(p);
                        } else if (value) {
                          handleLinkPhase(parseInt(value), p.id);
                        }
                      }}
                    >
                      <SelectTrigger className="h-7 text-xs">
                        <SelectValue placeholder="Assign to..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="holding_costs" className="text-purple-600 font-medium">
                          Holding Costs
                        </SelectItem>
                        <div className="border-t my-1" />
                        {constructionPhases.map(cp => (
                          <SelectItem key={cp.phase_number} value={cp.phase_number.toString()}>
                            Phase {cp.phase_number}: {cp.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 8-Phase Accordion */}
      <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
        <CardContent className="p-0">
          {/* Pre-Project Expenses Quick Action - hides automatically once user has at least one prepaid expense */}
          {!isReadOnly && !hasPrepaidExpenses && (
            <div
              className="px-6 py-4 border-b border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 flex items-center justify-between flex-wrap gap-3"
              data-testid="prepaid-expenses-banner"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Receipt className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <p className="font-semibold text-amber-900">Already paid for permits, architects, or surveys?</p>
                  <p className="text-sm text-amber-700">
                    Add them in 30 seconds — they'll skip lien-waiver requirements and count toward your draws.
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => setIsPrePaidDialogOpen(true)}
                className="bg-amber-600 hover:bg-amber-700 text-white shadow-sm"
                data-testid="open-prepaid-expenses-dialog"
              >
                <Plus className="w-4 h-4 mr-1" /> Bulk Import Pre-Project Expenses
              </Button>
            </div>
          )}
          {/* v2.7 — Budget is table-only (Cards view retired per owner request) */}
          <div className="flex items-center justify-end gap-3 mb-3 flex-wrap" data-testid="budget-view-toggle">
            {budgetView === 'table' && (
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="budget-table-filters">
                {[{ id: 'all', label: 'All' }, ...PHASE_CATEGORIES].map((c) => (
                  <button key={c.id} onClick={() => setTableCatFilter(c.id)} data-testid={`budget-table-chip-${c.id}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${tableCatFilter === c.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
                    {c.id !== 'all' && <span className={`w-2 h-2 rounded-full ${catMeta(c.id).dot}`} />}{c.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {budgetView === 'table' ? (
            (() => {
              const catActive = (p) => (tableCatFilter === 'all' ? true : categoryOf(p) === tableCatFilter);
              // Project-level contingency remaining (drives the draw-readiness budget factor).
              const totalEstAll = budgetPhases.reduce((s, p) => s + (p.estimated_cost || 0), 0);
              const totalActAll = budgetPhases.reduce((s, p) => s + (p.actual_cost || 0), 0);
              const contBudget = project?.contingency_budget || 0;
              const contUsed = Math.min(totalActAll > totalEstAll ? totalActAll - totalEstAll : 0, contBudget);
              const contRemaining = contBudget - contUsed;
              let projEst = 0; let projAct = 0;
              const renderRow = (p) => {
                const est = p.estimated_cost || 0;
                const act = p.actual_cost || 0;
                const c = catMeta(categoryOf(p));
                const contractor = contractors.find((x) => x.id === p.contractor_id) || null;
                const totalCost = act || est;
                const amountPaid = p.supplier_amount_paid || 0;
                // A phase explicitly reopened via Reopen/Unmark carries
                // unmarked_paid_at + payment_status 'open'. We KEEP its payment
                // records (supplier_amount_paid lingers), so don't let the
                // amount-based inference flip it back to "Paid" — honor the
                // explicit reopen so the row shows the reopened / Pay-More state.
                const wasReopened = !!p.unmarked_paid_at && p.payment_status === 'open';
                const isPaid = p.supplier_paid || p.is_paid || (!wasReopened && totalCost > 0 && amountPaid >= totalCost);
                const vendorLabel = contractor ? (contractor.company || contractor.name) : (p.supplier_vendor || p.vendor || '—');
                const readiness = computeDrawReadiness(p, contractor, contRemaining);
                const itemReqs = effectiveRequirements(p);
                return (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`budget-table-row-${p.id}`}>
                    <td className="px-4 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${c.chip}`}>{c.label}</span></td>
                    <td className="px-4 py-3"><div className="font-medium text-slate-800">{p.name}</div><div className="text-xs text-slate-500">{vendorLabel}</div></td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-700">{fmtMoney(est)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                      {act ? fmtMoney(act) : '—'}
                      {est > 0 && act > 0 && (
                        act > est
                          ? <div className="text-[10px] font-medium text-red-600" data-testid={`budget-status-${p.id}`}>▲ Over by {fmtMoney(act - est)}</div>
                          : <div className="text-[10px] font-medium text-green-600" data-testid={`budget-status-${p.id}`}>{act === est ? '● On budget' : `▼ Under by ${fmtMoney(est - act)}`}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">{isPaid ? <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Paid</Badge> : (p.status && p.status !== 'pending' ? <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{String(p.status).replace('_', ' ')}</Badge> : <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200">pending</Badge>)}</td>
                    <td className="px-4 py-3"><DrawReadyChip readiness={readiness} requirements={itemReqs} editable={!isReadOnly} onToggleRequirement={(key, val) => updateItemRequirement(p, key, val)} testId={`budget-table-ready-${p.id}`} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {!isReadOnly && onPayContractor && !isPaid && (
                          <Button size="sm" variant="outline" className="h-7 text-xs border-green-500 text-green-600 hover:bg-green-50" onClick={() => onPayContractor(p, contractor)} data-testid={`budget-table-pay-${p.id}`}>
                            <CreditCard className="w-3 h-3 mr-1" />{amountPaid > 0 ? 'Pay More' : 'Pay'}
                          </Button>
                        )}
                        {!isReadOnly && onPayContractor && isPaid && (
                          <Button size="sm" variant="outline" className="h-7 text-xs border-amber-500 text-amber-700 hover:bg-amber-50" onClick={() => handleQuickUnmarkPaid(p)} data-testid={`reopen-paid-${p.id}`} title="Reopen this paid item so you can request a lien waiver, pay more, or make corrections">
                            <RotateCcw className="w-3 h-3 mr-1" />Reopen
                          </Button>
                        )}
                        {renderDocsBtn(p)}
                        {renderQuickDrawBtn(p)}
                        {!isReadOnly && contractor && (
                          <Button size="sm" variant="outline" className="h-7 text-xs border-purple-500 text-purple-600 hover:bg-purple-50" onClick={() => openPhaseInspections(p)} data-testid={`budget-table-inspection-${p.id}`}>
                            <ClipboardCheck className="w-3 h-3 mr-1" />Inspection
                          </Button>
                        )}
                        {!isReadOnly && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500 hover:text-blue-700" onClick={() => openFullEditDialog(p)} data-testid={`budget-table-edit-${p.id}`}>
                            <Pencil className="w-3 h-3 mr-1" />Edit
                          </Button>
                        )}
                        {!isReadOnly && (
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50" onClick={() => handleDeletePhase(p.id)} data-testid={`budget-table-delete-${p.id}`} title="Delete item">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              };
              const tableHead = (
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                    <th className="px-4 py-2.5">Category</th>
                    <th className="px-4 py-2.5">Item</th>
                    <th className="px-4 py-2.5 text-right">Estimated</th>
                    <th className="px-4 py-2.5 text-right">Actual</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5">DrawReady</th>
                    <th className="px-4 py-2.5 text-right">Action</th>
                  </tr>
                </thead>
              );
              const assignedIds = new Set(constructionPhases.flatMap((cp) => cp.linked_phase_ids || []));
              const unassignedRows = budgetPhases
                .filter((p) => !assignedIds.has(p.id))
                .filter(catActive)
                .sort((a, b) => categoryOf(a).localeCompare(categoryOf(b)));
              const uEst = unassignedRows.reduce((s, p) => s + (p.estimated_cost || 0), 0);
              const uAct = unassignedRows.reduce((s, p) => s + (p.actual_cost || 0), 0);
              projEst += uEst; projAct += uAct;
              return (
                <div className="space-y-3" data-testid="budget-table">
                  {constructionPhases.map((cp) => {
                    const linked = budgetPhases.filter((p) => (cp.linked_phase_ids || []).includes(p.id));
                    const rows = linked
                      .filter(catActive)
                      .sort((a, b) => categoryOf(a).localeCompare(categoryOf(b)));
                    const pEst = rows.reduce((s, p) => s + (p.estimated_cost || 0), 0);
                    const pAct = rows.reduce((s, p) => s + (p.actual_cost || 0), 0);
                    projEst += pEst; projAct += pAct;
                    const collapsed = collapsedTablePhases.has(cp.phase_number);
                    return (
                      <div key={cp.phase_number} className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`budget-table-phase-${cp.phase_number}`}>
                        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200">
                          <button
                            type="button"
                            onClick={() => toggleTablePhase(cp.phase_number)}
                            className="flex items-center gap-2 font-semibold text-slate-800 text-sm hover:text-blue-700"
                            data-testid={`budget-table-phase-toggle-${cp.phase_number}`}
                          >
                            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            Phase {cp.phase_number}{cp.name ? ` · ${cp.name}` : ''}
                          </button>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-slate-500 tabular-nums">Est {fmtMoney(pEst)}{pAct ? ` · Actual ${fmtMoney(pAct)}` : ''}</span>
                            {!isReadOnly && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                                onClick={() => { setAddItemType('material'); setAddItemPhaseNumber(cp.phase_number); setIsAddItemDialogOpen(true); }}
                                data-testid={`budget-table-add-${cp.phase_number}`}
                              >
                                <Plus className="w-3.5 h-3.5 mr-1" /> Add
                              </Button>
                            )}
                          </div>
                        </div>
                        {!collapsed && (
                          <div className="overflow-x-auto">
                            {rows.length === 0 ? (
                              <div className="py-6 text-center text-slate-400 text-xs">No {tableCatFilter === 'all' ? '' : `${catMeta(tableCatFilter).label.toLowerCase()} `}line items in this phase</div>
                            ) : (
                              <table className="w-full text-sm min-w-[720px]">
                                {tableHead}
                                <tbody>
                                  {rows.map(renderRow)}
                                  <tr className="bg-slate-50/70 font-medium text-slate-700" data-testid={`budget-table-subtotal-${cp.phase_number}`}>
                                    <td className="px-4 py-2"></td>
                                    <td className="px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Phase {cp.phase_number} subtotal</td>
                                    <td className="px-4 py-2 text-right tabular-nums">{fmtMoney(pEst)}</td>
                                    <td className="px-4 py-2 text-right tabular-nums">{fmtMoney(pAct)}</td>
                                    <td colSpan={3}></td>
                                  </tr>
                                </tbody>
                              </table>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {unassignedRows.length > 0 && (
                    <div className="bg-white rounded-2xl border border-amber-200 overflow-hidden" data-testid="budget-table-unassigned">
                      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-50 border-b border-amber-200">
                        <span className="font-semibold text-amber-800 text-sm">Unassigned Items</span>
                        <span className="text-xs text-amber-600 tabular-nums">Est {fmtMoney(uEst)}{uAct ? ` · Actual ${fmtMoney(uAct)}` : ''}</span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[720px]">
                          {tableHead}
                          <tbody>
                            {unassignedRows.map(renderRow)}
                            <tr className="bg-slate-50/70 font-medium text-slate-700" data-testid="budget-table-subtotal-unassigned">
                              <td className="px-4 py-2"></td>
                              <td className="px-4 py-2 text-xs uppercase tracking-wide text-slate-500">Unassigned subtotal</td>
                              <td className="px-4 py-2 text-right tabular-nums">{fmtMoney(uEst)}</td>
                              <td className="px-4 py-2 text-right tabular-nums">{fmtMoney(uAct)}</td>
                              <td colSpan={3}></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-slate-900 text-white" data-testid="budget-table-total">
                    <span className="font-semibold text-sm">Project total{tableCatFilter !== 'all' ? ` · ${catMeta(tableCatFilter).label}` : ''}</span>
                    <span className="text-sm tabular-nums">Est {fmtMoney(projEst)} · Actual {fmtMoney(projAct)} · <span className="text-emerald-300">Variance {fmtMoney(projEst - projAct)}</span></span>
                  </div>
                </div>
              );
            })()
          ) : (

          <Accordion type="multiple" value={expandedPhases} onValueChange={setExpandedPhases}>
            {constructionPhases.map((cp) => {
              const linkedPhases = budgetPhases.filter(p => (cp.linked_phase_ids || []).includes(p.id));
              const materialPhases = linkedPhases.filter(p => p.phase_type === 'material');
              const laborPhases = linkedPhases.filter(p => p.phase_type === 'labor' || p.phase_type === 'labor_material');
              const softCostPhases = linkedPhases.filter(p => p.phase_type === 'soft_cost');
              const inspections = cp.inspections || [];
              const isComplete = cp.progress_percent >= 100;
              
              const phaseBudget = linkedPhases.reduce((sum, p) => sum + (p.estimated_cost || 0), 0);
              const phaseActual = linkedPhases.reduce((sum, p) => {
                const actualCost = p.actual_cost || 0;
                const returnAmount = p.phase_type === 'material' ? (p.return_refund_amount || 0) : 0;
                return sum + actualCost - returnAmount;
              }, 0);
              // v2.5.10 (Option C): "Paid So Far" = money out the door against this phase.
              // Same math the dashboard's Total Paid uses, scoped to this phase's line items.
              // For materials we use supplier_amount_paid (falls back to actual_cost when
              // supplier_paid=true); for labor/soft costs we use supplier_amount_paid
              // (falls back to actual_cost when is_paid=true). Pre-paid soft costs are
              // counted at their pre-paid amount.
              const phasePaid = linkedPhases.reduce((sum, p) => {
                if (p.is_prepaid) return sum + (p.prepaid_amount || p.actual_cost || 0);
                if (p.phase_type === 'material') {
                  return sum + (p.supplier_amount_paid || (p.supplier_paid ? (p.actual_cost || 0) : 0));
                }
                return sum + (p.supplier_amount_paid || (p.is_paid ? (p.actual_cost || 0) : 0));
              }, 0);
              const paidPct = phaseActual > 0 ? Math.min(100, Math.round((phasePaid / phaseActual) * 100)) : 0;
              
              const PhaseIcon = PHASE_ICON_COMPONENTS[cp.phase_number] || Circle;
              
              return (
                <AccordionItem 
                  key={cp.phase_number} 
                  value={`phase-${cp.phase_number}`}
                  className="border-b last:border-b-0"
                  data-testid={`construction-phase-${cp.phase_number}`}
                >
                  <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-slate-50 group">
                    <div className="flex items-center gap-4 w-full">
                      {/* Phase Icon - Using Lucide icons */}
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        isComplete ? 'bg-green-100' : cp.status === 'in_progress' ? 'bg-blue-100' : 'bg-slate-100'
                      }`}>
                        {isComplete ? (
                          <CheckCircle2 className="w-6 h-6 text-green-600" />
                        ) : (
                          <PhaseIcon className={`w-6 h-6 ${cp.status === 'in_progress' ? 'text-blue-600' : 'text-slate-400'}`} />
                        )}
                      </div>
                      
                      {/* Phase Info */}
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-800">
                            Phase {cp.phase_number}: {cp.name}
                          </span>
                          <Badge 
                            variant={isComplete ? 'default' : 'outline'}
                            className={isComplete ? 'bg-green-100 text-green-700' : ''}
                          >
                            {cp.status === 'completed' ? 'Complete' : cp.status === 'in_progress' ? 'In Progress' : 'Not Started'}
                          </Badge>
                          {/* Edit Phase Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 hover:bg-slate-200"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditPhaseDialog(cp);
                            }}
                            title="Edit phase name and description"
                          >
                            <Pencil className="w-3 h-3 text-slate-500" />
                          </Button>
                        </div>
                        {/* Phase Description - Always visible */}
                        <p className="text-sm text-slate-600 mt-0.5 italic">
                          {cp.description}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                          <span><Package className="w-3 h-3 inline mr-1" />{materialPhases.length} materials</span>
                          <span><Wrench className="w-3 h-3 inline mr-1" />{laborPhases.length} labor</span>
                          <span><Receipt className="w-3 h-3 inline mr-1" />{softCostPhases.length} soft costs</span>
                          <span><ClipboardCheck className="w-3 h-3 inline mr-1" />{inspections.filter(i => i.status === 'passed').length}/{inspections.length} permits/inspections</span>
                          <span><DollarSign className="w-3 h-3 inline mr-1" />${phaseBudget.toLocaleString()}</span>
                        </div>
                      </div>
                      
                      {/* Progress */}
                      <div className="w-28 mr-4">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className={`font-semibold ${isComplete ? 'text-green-600' : 'text-slate-700'}`}>
                            {cp.progress_percent}%
                          </span>
                        </div>
                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${getProgressColor(cp.progress_percent)}`}
                            style={{ width: `${cp.progress_percent}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  
                  <AccordionContent className="px-6 pb-6">
                    {/* Per-Phase Financial Summary */}
                    {/* v2.5.10 (Option C): three bubbles — Phase Budget (planned), Actual Cost (contracted, with Paid subtext), Variance (Budget vs Contracted). */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6 p-4 bg-slate-50 rounded-xl">
                      <div className="min-w-0">
                        <p className="text-xs text-slate-500 font-medium">Phase Budget</p>
                        <p className="text-lg font-bold text-slate-800 tabular-nums">${phaseBudget.toLocaleString()}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">Planned</p>
                      </div>
                      <div className="min-w-0" data-testid={`phase-actual-cost-${cp.phase_number}`}>
                        <p className="text-xs text-slate-500 font-medium">Actual Cost</p>
                        <p className="text-lg font-bold text-slate-800 tabular-nums">${phaseActual.toLocaleString()}</p>
                        <p
                          className={`text-[11px] mt-0.5 ${phasePaid >= phaseActual && phaseActual > 0 ? 'text-emerald-700' : 'text-slate-500'}`}
                          data-testid={`phase-paid-so-far-${cp.phase_number}`}
                        >
                          Paid ${phasePaid.toLocaleString()}{phaseActual > 0 ? ` (${paidPct}%)` : ''}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-slate-500 font-medium">Variance</p>
                        <p className={`text-lg font-bold tabular-nums ${phaseBudget - phaseActual >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {phaseBudget - phaseActual >= 0 ? '+' : ''}${(phaseBudget - phaseActual).toLocaleString()}
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5">Budget − Contracted</p>
                      </div>
                    </div>
                    
                    {/* Phase Checklist Button */}
                    {getPhaseChecklist(cp.phase_number) && (
                      <div className="mb-6">
                        <Button
                          variant="outline"
                          className="w-full border-dashed border-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50 hover:border-indigo-400"
                          onClick={() => {
                            setChecklistPhaseNumber(cp.phase_number);
                            setChecklistPhaseName(cp.name);
                            setIsPhaseChecklistOpen(true);
                          }}
                          data-testid={`view-checklist-btn-${cp.phase_number}`}
                        >
                          <ClipboardCheck className="w-4 h-4 mr-2" />
                          View Phase Checklist
                          <span className="ml-2 text-xs text-indigo-500">
                            ({getPhaseChecklist(cp.phase_number)?.items?.length || 0} items)
                          </span>
                        </Button>
                      </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Materials Section */}
                      <div className="bg-amber-50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Package className="w-5 h-5 text-amber-600" />
                            <h4 className="font-semibold text-amber-800">Materials</h4>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-amber-700 hover:bg-amber-100"
                            onClick={() => {
                              setAddItemType('material');
                              setAddItemPhaseNumber(cp.phase_number);
                              setIsAddItemDialogOpen(true);
                            }}
                            data-testid={`add-material-btn-${cp.phase_number}`}
                            disabled={isReadOnly}
                            style={isReadOnly ? { display: 'none' } : {}}
                          >
                            <Plus className="w-4 h-4 mr-1" /> Add
                          </Button>
                        </div>
                        
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                          {materialPhases.map(p => renderMaterialItem(p, cp.phase_number))}
                          {materialPhases.length === 0 && (
                            <p className="text-sm text-amber-600 italic text-center py-4">No materials added</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Labor Section */}
                      <div className="bg-blue-50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Wrench className="w-5 h-5 text-blue-600" />
                            <h4 className="font-semibold text-blue-800">Labor</h4>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-blue-700 hover:bg-blue-100"
                            onClick={() => {
                              setAddItemType('labor');
                              setAddItemPhaseNumber(cp.phase_number);
                              setIsAddItemDialogOpen(true);
                            }}
                            data-testid={`add-labor-btn-${cp.phase_number}`}
                            disabled={isReadOnly}
                            style={isReadOnly ? { display: 'none' } : {}}
                          >
                            <Plus className="w-4 h-4 mr-1" /> Add
                          </Button>
                        </div>
                        
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                          {laborPhases.map(p => renderLaborItem(p, cp.phase_number))}
                          {laborPhases.length === 0 && (
                            <p className="text-sm text-blue-600 italic text-center py-4">No labor added</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Soft Costs Section */}
                      <div className="bg-teal-50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Receipt className="w-5 h-5 text-teal-600" />
                            <h4 className="font-semibold text-teal-800">Soft Costs</h4>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-teal-700 hover:bg-teal-100"
                            onClick={() => {
                              setAddItemPhaseNumber(cp.phase_number);
                              setAddItemType('soft_cost');
                              setIsAddItemDialogOpen(true);
                            }}
                            data-testid={`add-soft-cost-btn-${cp.phase_number}`}
                            disabled={isReadOnly}
                            style={isReadOnly ? { display: 'none' } : {}}
                          >
                            <Plus className="w-4 h-4 mr-1" /> Add
                          </Button>
                        </div>
                        
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                          {softCostPhases.map(p => (
                            <div key={p.id} id={`phase-row-${p.id}`} data-phase-id={p.id} className="bg-white rounded-lg p-3 shadow-sm">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm truncate flex-1">{p.name}</span>
                                <div className="flex items-center gap-2">
                                  {p.is_prepaid && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs bg-amber-50 text-amber-700 border-amber-300"
                                      data-testid={`soft-cost-prepaid-badge-${p.id}`}
                                      title="Pre-paid before project start (no lien waiver required)"
                                    >
                                      Pre-Paid
                                    </Badge>
                                  )}
                                  <Badge 
                                    variant="outline" 
                                    className={`text-xs ${
                                      p.status === 'completed' || p.paid || p.is_paid ? 'bg-green-50 text-green-700 border-green-200' :
                                      p.status === 'in_progress' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                      'bg-slate-50 text-slate-600 border-slate-200'
                                    }`}
                                  >
                                    {p.is_paid || p.paid ? 'Paid' : p.status === 'completed' ? 'Completed' : p.status === 'in_progress' ? 'In Progress' : 'Pending'}
                                  </Badge>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" data-testid={`soft-cost-menu-${p.id}`}>
                                        <MoreVertical className="w-4 h-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent>
                                      <DropdownMenuItem onClick={() => openFullEditDialog(p)} data-testid={`soft-cost-edit-${p.id}`}>
                                        <Pencil className="w-4 h-4 mr-2" /> Edit
                                      </DropdownMenuItem>
                                      <DropdownMenuItem 
                                        onClick={() => handleDeletePhase(p.id)}
                                        className="text-red-600"
                                        data-testid={`soft-cost-delete-${p.id}`}
                                      >
                                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </div>
                              <div className="flex items-center justify-between mt-2 text-xs text-teal-600">
                                <span>Est: ${(p.estimated_cost || 0).toLocaleString()}</span>
                                <span>Actual: ${(p.actual_cost || 0).toLocaleString()}</span>
                              </div>
                              {(p.supplier_vendor || p.vendor) && (
                                <p className="text-xs text-slate-500 mt-1">
                                  <User className="w-3 h-3 inline mr-1" />{p.supplier_vendor || p.vendor}
                                </p>
                              )}
                              {p.is_prepaid && p.prepaid_date && (
                                <p className="text-xs text-amber-700 mt-1">
                                  Paid on {formatDate(p.prepaid_date)}
                                </p>
                              )}
                              {p.bank_category && (
                                <Badge variant="outline" className="text-xs mt-1 bg-teal-50 text-teal-700 border-teal-200">
                                  {p.bank_category}
                                </Badge>
                              )}
                              {/* v2.4: Pay button for soft costs (engineers/architects/permits)
                                  Triggers same payment flow as labor. If requires_compliance
                                  is true (engineer/architect), the drawer's step 3 will
                                  show lien-waiver-only compliance like other vendor work.
                                  If false (permit/city fee), compliance step is hidden. */}
                              {!isReadOnly && !p.is_prepaid && onPayContractor && (() => {
                                const totalCost = p.actual_cost || p.estimated_cost || 0;
                                const amountPaid = p.supplier_amount_paid || 0;
                                const stillOwes = amountPaid < totalCost;
                                const fullyPaid = p.is_paid && !stillOwes;
                                return (
                                  <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                                    {!fullyPaid && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 text-xs border-green-500 text-green-600 hover:bg-green-50"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // Resolve contractor lazily — soft costs may or may not have one
                                          const c = contractors.find(c => c.id === p.contractor_id) || null;
                                          onPayContractor(p, c);
                                        }}
                                        data-testid={`soft-cost-pay-${p.id}`}
                                      >
                                        <CreditCard className="w-3 h-3 mr-1" /> {amountPaid > 0 ? 'Pay More' : 'Pay'}
                                      </Button>
                                    )}
                                    {fullyPaid && (
                                      <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                                        ✓ Paid
                                      </Badge>
                                    )}
                                    {/* Compliance toggle: tells the system whether to gate
                                        this soft cost on lien-waiver/W9 like other vendor
                                        work. Default: false (permits/city fees). */}
                                    {p.requires_compliance ? (
                                      <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200" title="Lien waiver + W9 required at payment">
                                        Compliance on
                                      </Badge>
                                    ) : (
                                      <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200" title="No compliance docs required (e.g., city/permit fees)">
                                        Compliance off
                                      </Badge>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          ))}
                          {softCostPhases.length === 0 && (
                            <p className="text-sm text-teal-600 italic text-center py-4">No soft costs added</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Permits & Inspections Section */}
                      <div className="bg-purple-50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <ClipboardCheck className="w-5 h-5 text-purple-600" />
                            <h4 className="font-semibold text-purple-800">Permits & Inspections</h4>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-purple-700 hover:bg-purple-100"
                            onClick={() => {
                              setAddInspectionPhaseNumber(cp.phase_number);
                              setIsAddInspectionDialogOpen(true);
                            }}
                            data-testid={`add-inspection-btn-${cp.phase_number}`}
                          >
                            <Plus className="w-4 h-4 mr-1" /> Add
                          </Button>
                        </div>
                        
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                          {inspections.map(insp => (
                            <div key={insp.id} className="bg-white rounded-lg p-3 shadow-sm">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm truncate flex-1">{insp.name}</span>
                                <div className="flex items-center gap-1">
                                  {insp.status === 'passed' ? (
                                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                                  ) : (
                                    <Circle className="w-4 h-4 text-slate-300" />
                                  )}
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                        <MoreVertical className="w-4 h-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent>
                                      {insp.file_id && (
                                        <DropdownMenuItem onClick={() => openPreview(`/api/files/${insp.file_id}`, 'Inspection Report')}>
                                          <Eye className="w-4 h-4 mr-2" /> View Report
                                        </DropdownMenuItem>
                                      )}
                                      <DropdownMenuItem onClick={() => handleDeleteInspection(cp.phase_number, insp.id)}>
                                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </div>
                              <p className="text-xs text-purple-600 mt-1">
                                {INSPECTION_TYPE_LABELS[insp.type] || insp.type}
                              </p>
                              
                              {insp.status !== 'passed' && (
                                <div className="mt-2 flex items-center gap-2">
                                  <label className="flex-1">
                                    <input
                                      type="file"
                                      className="hidden"
                                      onChange={(e) => {
                                        if (e.target.files?.[0]) {
                                          handleUploadInspectionFile(cp.phase_number, insp.id, e.target.files[0]);
                                        }
                                      }}
                                    />
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="w-full text-xs h-7"
                                      disabled={uploadingInspectionId === insp.id}
                                      asChild
                                    >
                                      <span>
                                        <Upload className="w-3 h-3 mr-1" />
                                        {uploadingInspectionId === insp.id ? 'Uploading...' : 'Upload Report'}
                                      </span>
                                    </Button>
                                  </label>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-xs h-7 text-slate-500"
                                    onClick={() => handleOverrideInspection(cp.phase_number, insp.id)}
                                    title="Mark as passed without uploading"
                                  >
                                    Override
                                  </Button>
                                </div>
                              )}
                              
                              {insp.status === 'passed' && (
                                <div className="mt-1 flex items-center gap-2 flex-wrap">
                                  <Badge className="bg-green-100 text-green-700 text-xs">
                                    {insp.override ? 'Passed (Override)' : 'Passed'}
                                  </Badge>
                                  {insp.file_id ? (
                                    <>
                                      <Badge variant="outline" className="text-xs">
                                        <FileText className="w-3 h-3 mr-1" /> Report
                                      </Badge>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-xs h-7"
                                        onClick={() => openPreview(`/api/files/${insp.file_id}`, 'Inspection Report')}
                                        data-testid={`inspection-view-${insp.id}`}
                                      >
                                        <Eye className="w-3 h-3 mr-1" /> View / Download
                                      </Button>
                                    </>
                                  ) : (
                                    <label>
                                      <input
                                        type="file"
                                        className="hidden"
                                        onChange={(e) => {
                                          if (e.target.files?.[0]) {
                                            handleUploadInspectionFile(cp.phase_number, insp.id, e.target.files[0]);
                                          }
                                        }}
                                      />
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-xs h-7"
                                        disabled={uploadingInspectionId === insp.id}
                                        asChild
                                        data-testid={`inspection-attach-${insp.id}`}
                                      >
                                        <span>
                                          <Upload className="w-3 h-3 mr-1" />
                                          {uploadingInspectionId === insp.id ? 'Uploading...' : 'Attach Report'}
                                        </span>
                                      </Button>
                                    </label>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                          {inspections.length === 0 && (
                            <p className="text-sm text-purple-600 italic text-center py-4">No inspections added</p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Link Unassigned Items */}
                    {unassignedPhases.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <div className="flex items-center gap-3">
                          <Select
                            value=""
                            onValueChange={(phaseId) => {
                              if (phaseId) handleLinkPhase(cp.phase_number, phaseId);
                            }}
                          >
                            <SelectTrigger className="w-72">
                              <SelectValue placeholder="Link an existing budget item..." />
                            </SelectTrigger>
                            <SelectContent>
                              {unassignedPhases.map(p => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name} ({p.phase_type || 'general'}) - ${(p.estimated_cost || 0).toLocaleString()}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
          )}
        </CardContent>
      </Card>

      {/* Add Material/Labor/Soft Cost Dialog */}
      <Dialog open={isAddItemDialogOpen} onOpenChange={setIsAddItemDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Add {addItemType === 'material' ? 'Material' : addItemType === 'soft_cost' ? 'Soft Cost' : addItemType === 'labor_material' ? 'Labor & Materials' : 'Labor'} to Phase {addItemPhaseNumber}
            </DialogTitle>
            <DialogDescription>
              {addItemType === 'material' 
                ? 'Add a material or supply item to this construction phase.'
                : addItemType === 'soft_cost'
                ? 'Add a soft cost item (permits, fees, professional services) to this phase.'
                : addItemType === 'labor_material'
                ? 'Add a turnkey item where labor and materials are billed together.'
                : 'Add a labor or contractor work item to this construction phase.'}
            </DialogDescription>
          </DialogHeader>

          {/* v2.7 — item type selector (Material / Labor / Labor & Materials / Soft Cost) */}
          <div className="flex flex-wrap gap-1.5" data-testid="add-item-type-selector">
            {[
              { id: 'material', label: 'Material' },
              { id: 'labor', label: 'Labor' },
              { id: 'labor_material', label: 'Labor & Materials' },
              { id: 'soft_cost', label: 'Soft Cost' },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setAddItemType(t.id)}
                data-testid={`add-item-type-${t.id}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${addItemType === t.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          
          {/* AI Receipt Extraction */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-sm text-slate-800">AI Receipt Scanner</p>
                  <p className="text-xs text-slate-500">Upload a receipt to auto-fill the form</p>
                </div>
              </div>
              <label className="cursor-pointer">
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={isExtractingReceipt}
                  className="bg-white"
                  asChild
                >
                  <span>
                    {isExtractingReceipt ? (
                      <>
                        <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full mr-2" />
                        Extracting...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Receipt
                      </>
                    )}
                  </span>
                </Button>
                <input
                  type="file"
                  className="hidden"
                  accept=".jpg,.jpeg,.png,.webp,.pdf,.heic,.heif,.gif,.tif,.tiff"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleExtractReceiptForAddItem(e.target.files[0]);
                    }
                  }}
                  disabled={isExtractingReceipt}
                />
              </label>
            </div>
            {addItemReceiptFile && (
              <div className="mt-2 flex items-center gap-2 text-xs text-green-600">
                <FileText className="w-4 h-4" />
                <span>{addItemReceiptFile.name}</span>
              </div>
            )}
          </div>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="item-name">Name *</Label>
              <Input
                id="item-name"
                placeholder={addItemType === 'material' ? 'e.g., Lumber, Concrete' : addItemType === 'soft_cost' ? 'e.g., Permit Fee, Architect' : 'e.g., Framing Labor'}
                value={newItem.name}
                onChange={(e) => setNewItem({...newItem, name: e.target.value})}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="item-cost">Estimated Cost *</Label>
                <Input
                  id="item-cost"
                  type="number"
                  placeholder="0.00"
                  value={newItem.estimated_cost}
                  onChange={(e) => setNewItem({...newItem, estimated_cost: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="item-start">Start Date</Label>
                <Input
                  id="item-start"
                  type="date"
                  value={newItem.start_date}
                  onChange={(e) => setNewItem({...newItem, start_date: e.target.value})}
                />
              </div>
            </div>

            {/* Bank Draw Category */}
            <div>
              <Label>Bank Draw Category</Label>
              <Select
                value={newItem.bank_category}
                onValueChange={(val) => setNewItem({...newItem, bank_category: val})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select bank category..." />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {HILLS_BANK_CATEGORIES.map(cat => (
                    <SelectItem key={cat.code} value={cat.fullName}>
                      {cat.fullName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {addItemType === 'material' && (
              <div>
                <Label htmlFor="item-supplier">Supplier/Vendor</Label>
                <Input
                  id="item-supplier"
                  data-testid="add-item-supplier"
                  placeholder="e.g., Home Depot, ABC Supply"
                  value={newItem.supplier_vendor}
                  onChange={(e) => setNewItem({...newItem, supplier_vendor: e.target.value})}
                />
              </div>
            )}
            
            {addItemType === 'soft_cost' && (
              <>
                <div>
                  <Label htmlFor="item-vendor">Vendor/Provider</Label>
                  <Input
                    id="item-vendor"
                    data-testid="add-item-vendor"
                    placeholder="e.g., City of Cedar Rapids, Architect Name"
                    value={newItem.vendor}
                    onChange={(e) => setNewItem({...newItem, vendor: e.target.value})}
                  />
                </div>
                {/* v2.4: Compliance toggle — some soft costs need lien waiver / W9
                    (engineers, architects, subcontracted soft services), others
                    don't (city permit fees, government inspections). */}
                <div className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-md">
                  <Checkbox
                    id="item-requires-compliance"
                    checked={!!newItem.requires_compliance}
                    onCheckedChange={(v) => setNewItem({ ...newItem, requires_compliance: !!v })}
                    data-testid="soft-cost-requires-compliance-toggle"
                    className="mt-0.5"
                  />
                  <label htmlFor="item-requires-compliance" className="text-sm cursor-pointer">
                    <span className="font-medium text-slate-900">Requires compliance docs at payment</span>
                    <span className="block text-xs text-slate-500 mt-0.5">
                      Check this for engineers, architects, or any vendor that signs a lien waiver.
                      Leave unchecked for permits, city fees, and other no-compliance soft costs.
                    </span>
                  </label>
                </div>
              </>
            )}
            
            {(addItemType === 'labor' || addItemType === 'labor_material') && (
              <div>
                <Label htmlFor="item-contractor" className="flex items-center gap-1">
                  Contractor <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={newItem.contractor_id}
                  onValueChange={(val) => setNewItem({...newItem, contractor_id: val})}
                >
                  <SelectTrigger data-testid="add-item-contractor" className={!newItem.contractor_id ? "border-amber-300" : ""}>
                    <SelectValue placeholder="Select contractor (required)..." />
                  </SelectTrigger>
                  <SelectContent>
                    {contractors.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.company || c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!newItem.contractor_id && (
                  <p className="text-xs text-amber-600 mt-1">A contractor must be assigned to labor items</p>
                )}
              </div>
            )}
            
            <div>
              <Label htmlFor="item-description">Description</Label>
              <Textarea
                id="item-description"
                placeholder="Optional description..."
                value={newItem.description}
                onChange={(e) => setNewItem({...newItem, description: e.target.value})}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddItemDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddItem}>
              Add {addItemType === 'material' ? 'Material' : addItemType === 'soft_cost' ? 'Soft Cost' : addItemType === 'labor_material' ? 'Labor & Materials' : 'Labor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit {editingItem?.name}</DialogTitle>
          </DialogHeader>

          {/* v2.9 — category/type selector so bulk-imported items can be reclassified */}
          <div>
            <Label className="text-xs text-slate-500">Category</Label>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="edit-item-type-selector">
              {[
                { id: 'material', label: 'Material' },
                { id: 'labor', label: 'Labor' },
                { id: 'labor_material', label: 'Labor & Materials' },
                { id: 'soft_cost', label: 'Soft Cost' },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setEditFormData((prev) => ({ ...prev, phase_type: t.id }))}
                  data-testid={`edit-item-type-${t.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${editFormData.phase_type === t.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* v2.9.5 — Supplier/Vendor free text for Material only. Soft Cost has
              its own Vendor field below; Labor / Labor & Materials use the
              Contractor dropdown (no duplicate vendor field). */}
          {editFormData.phase_type === 'material' && (
          <div>
            <Label htmlFor="edit-item-vendor" className="text-xs text-slate-500">Supplier / Vendor</Label>
            <Input
              id="edit-item-vendor"
              value={editFormData.supplier_vendor || ''}
              onChange={(e) => setEditFormData({ ...editFormData, supplier_vendor: e.target.value })}
              placeholder="e.g. City of Cedar Rapids"
              data-testid="edit-item-vendor"
            />
          </div>
          )}


          
          {/* AI Receipt Extraction for Full Edit */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-blue-600" />
                <div>
                  <p className="font-medium text-sm text-slate-800">AI Receipt Scanner</p>
                  <p className="text-xs text-slate-500">Upload to auto-fill fields</p>
                </div>
              </div>
              <label className="cursor-pointer">
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={isExtractingReceipt}
                  className="bg-white h-8"
                  asChild
                >
                  <span>
                    {isExtractingReceipt ? (
                      <>
                        <div className="animate-spin w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full mr-1" />
                        Extracting...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-3 h-3 mr-1" />
                        Scan Receipt
                      </>
                    )}
                  </span>
                </Button>
                <input
                  type="file"
                  className="hidden"
                  accept=".jpg,.jpeg,.png,.webp,.pdf,.heic,.heif,.gif,.tif,.tiff"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    
                    setIsExtractingReceipt(true);
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      
                      const res = await apiService.extractReceiptData(formData);
                      
                      if (res.data.success && res.data.extracted_data) {
                        const data = res.data.extracted_data;
                        
                        // Try to match contractor from extracted vendor name
                        let matchedContractorId = editFormData.contractor_id;
                        if (data.vendor_name && contractors.length > 0) {
                          const vendorLower = data.vendor_name.toLowerCase();
                          const matchedContractor = contractors.find(c => {
                            const companyLower = (c.company || '').toLowerCase();
                            const nameLower = (c.name || '').toLowerCase();
                            return companyLower.includes(vendorLower) || 
                                   vendorLower.includes(companyLower) ||
                                   nameLower.includes(vendorLower) ||
                                   vendorLower.includes(nameLower);
                          });
                          if (matchedContractor) {
                            matchedContractorId = matchedContractor.id;
                          }
                        }
                        
                        setEditFormData(prev => ({
                          ...prev,
                          name: data.description || data.vendor_name || prev.name,
                          estimated_cost: data.total_amount || prev.estimated_cost,
                          actual_cost: data.total_amount || prev.actual_cost,
                          supplier_vendor: data.vendor_name || prev.supplier_vendor,
                          contractor_id: matchedContractorId,
                          description: data.line_items 
                            ? data.line_items.map(item => `${item.description}: $${item.amount}`).join('\n')
                            : (data.description || prev.description),
                          start_date: data.date || prev.start_date,
                          bank_category: data.suggested_bank_category || prev.bank_category
                        }));
                        
                        // Also upload the receipt file so it can be viewed later
                        if (editingItem?.id) {
                          try {
                            const uploadRes = await apiService.uploadPhaseReceiptFile(editingItem.id, file);
                            setBudgetPhases(prev => prev.map(p => 
                              p.id === editingItem.id ? { ...p, receipt_file_id: uploadRes.data.file_id, receipt_filename: uploadRes.data.filename } : p
                            ));
                            setEditingItem(prev => ({ ...prev, receipt_file_id: uploadRes.data.file_id, receipt_filename: uploadRes.data.filename }));
                          } catch (uploadErr) {
                            console.error('Receipt file upload failed:', uploadErr);
                          }
                        }
                        
                        toast.success('Receipt data extracted and saved!');
                      } else {
                        toast.error('Could not extract data');
                      }
                    } catch (err) {
                      toast.error('Extraction failed');
                    } finally {
                      setIsExtractingReceipt(false);
                    }
                  }}
                  disabled={isExtractingReceipt}
                />
              </label>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={editFormData.name}
                onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Estimated Cost</Label>
                <Input
                  type="number"
                  value={editFormData.estimated_cost}
                  onChange={(e) => setEditFormData({...editFormData, estimated_cost: e.target.value})}
                />
              </div>
              <div>
                <Label>Actual Cost</Label>
                <Input
                  type="number"
                  value={editFormData.actual_cost}
                  onChange={(e) => setEditFormData({...editFormData, actual_cost: e.target.value})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={editFormData.start_date}
                  onChange={(e) => setEditFormData({...editFormData, start_date: e.target.value})}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={editFormData.end_date}
                  onChange={(e) => setEditFormData({...editFormData, end_date: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <Label>Status</Label>
              <Select 
                value={editFormData.status} 
                onValueChange={(v) => setEditFormData({...editFormData, status: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bank Draw Category */}
            <div>
              <Label>Bank Draw Category</Label>
              <Select
                value={editFormData.bank_category}
                onValueChange={(val) => setEditFormData({...editFormData, bank_category: val})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select bank category..." />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="__none__">None</SelectItem>
                  {HILLS_BANK_CATEGORIES.map(cat => (
                    <SelectItem key={cat.code} value={cat.fullName}>
                      {cat.fullName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {(editFormData.phase_type === 'material' || editFormData.phase_type === 'labor_material') && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-green-600">Return/Refund Amount</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      className="border-green-300 focus:border-green-500"
                      value={editFormData.return_refund_amount || ''}
                      onChange={(e) => setEditFormData({...editFormData, return_refund_amount: e.target.value})}
                    />
                    <p className="text-xs text-slate-500 mt-1">Amount refunded from material returns</p>
                  </div>
                  <div>
                    <Label>Net Cost</Label>
                    <div className="h-9 flex items-center px-3 bg-slate-100 rounded-md font-medium">
                      ${((parseFloat(editFormData.actual_cost) || 0) - (parseFloat(editFormData.return_refund_amount) || 0)).toLocaleString()}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Actual cost minus refunds</p>
                  </div>
                </div>
              </>
            )}
            
            {(editFormData.phase_type === 'labor' || editFormData.phase_type === 'labor_material') && (
              <div>
                <Label>Contractor</Label>
                <Select 
                  value={editFormData.contractor_id} 
                  onValueChange={(v) => setEditFormData({...editFormData, contractor_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select contractor..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {contractors.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.company || c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Soft Cost specific fields */}
            {editFormData.phase_type === 'soft_cost' && (
              <>
                <div>
                  <Label>Vendor</Label>
                  <Input
                    value={editFormData.supplier_vendor}
                    onChange={(e) => setEditFormData({...editFormData, supplier_vendor: e.target.value})}
                    placeholder="e.g., Title Company, City Permits"
                  />
                </div>
                
                {/* Pre-Paid Option */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <input
                      type="checkbox"
                      id="is_prepaid"
                      checked={editFormData.is_prepaid || false}
                      onChange={(e) => setEditFormData({
                        ...editFormData, 
                        is_prepaid: e.target.checked,
                        // If marking as prepaid, also update status
                        status: e.target.checked ? 'completed' : editFormData.status,
                        is_paid: e.target.checked ? true : editFormData.is_paid
                      })}
                      className="w-4 h-4 rounded border-amber-400"
                    />
                    <Label htmlFor="is_prepaid" className="text-amber-800 font-medium cursor-pointer">
                      Already Paid (Pre-Project Expense)
                    </Label>
                  </div>
                  <p className="text-xs text-amber-700 ml-7">
                    Check this if this expense was paid before the project started. Pre-paid items don't require lien waivers.
                  </p>
                  
                  {editFormData.is_prepaid && (
                    <div className="mt-3 ml-7 space-y-3">
                      <div>
                        <Label className="text-amber-800">Amount Paid</Label>
                        <Input
                          type="number"
                          value={editFormData.prepaid_amount || editFormData.actual_cost || ''}
                          onChange={(e) => setEditFormData({
                            ...editFormData, 
                            prepaid_amount: e.target.value,
                            actual_cost: e.target.value
                          })}
                          className="border-amber-300"
                          placeholder="Enter amount already paid"
                        />
                      </div>
                      <div>
                        <Label className="text-amber-800">Payment Date</Label>
                        <Input
                          type="date"
                          value={editFormData.prepaid_date || ''}
                          onChange={(e) => setEditFormData({...editFormData, prepaid_date: e.target.value})}
                          className="border-amber-300"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={editFormData.description}
                onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
              />
            </div>
            
            {/* Receipts & Documentation Section */}
            {editingItem?.id && (
              <ReceiptsDocumentation
                phaseId={editingItem.id}
                phaseName={editingItem.name}
                isReadOnly={isReadOnly}
                onTotalChange={(total) => {
                  // Optionally track the total
                }}
                onAddToActualCost={(total) => {
                  setEditFormData(prev => ({
                    ...prev,
                    actual_cost: (parseFloat(prev.actual_cost) || 0) + total
                  }));
                }}
                onActualCostChanged={(newActualCost) => {
                  // v2.4: server atomically incremented actual_cost on upload —
                  // sync the local edit form so the user sees the new total.
                  setEditFormData(prev => ({
                    ...prev,
                    actual_cost: newActualCost
                  }));
                }}
              />
            )}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row sm:justify-between gap-2">
            <Button
              variant="ghost"
              className="text-red-600 hover:text-red-700 hover:bg-red-50 sm:mr-auto"
              onClick={() => { const id = editingItem?.id; setIsEditDialogOpen(false); if (id) handleDeletePhase(id); }}
              data-testid="edit-item-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete item
            </Button>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveFullEdit}>
                Save Changes
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Start Date Confirmation Dialog */}
      <Dialog open={isStartDialogOpen} onOpenChange={setIsStartDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirm Start Date</DialogTitle>
            <DialogDescription>
              When did "{startingItem?.name}" start?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Start Date</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsStartDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmStart}>
              <Play className="w-4 h-4 mr-2" />
              Start
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Complete Confirmation Dialog */}
      <Dialog open={isCompleteDialogOpen} onOpenChange={setIsCompleteDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="complete-phase-dialog">
          <DialogHeader>
            <DialogTitle>Mark as Complete</DialogTitle>
            <DialogDescription>
              When was "{completingItem?.name}" completed?
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Label>Completion date</Label>
            <Input
              type="date"
              value={completeDate}
              onChange={(e) => setCompleteDate(e.target.value)}
              data-testid="complete-date-input"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCompleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={confirmComplete} data-testid="confirm-complete-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Mark Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Add Inspection Dialog */}
      <Dialog open={isAddInspectionDialogOpen} onOpenChange={setIsAddInspectionDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Inspection to Phase {addInspectionPhaseNumber}</DialogTitle>
            <DialogDescription>
              Add an inspection, permit review, or other required approval.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="inspection-name">Name *</Label>
              <Input
                id="inspection-name"
                placeholder="e.g., Foundation Inspection, Permit Review"
                value={newInspection.name}
                onChange={(e) => setNewInspection({...newInspection, name: e.target.value})}
              />
            </div>
            
            <div>
              <Label htmlFor="inspection-type">Type</Label>
              <Select
                value={newInspection.type}
                onValueChange={(val) => setNewInspection({...newInspection, type: val})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(INSPECTION_TYPE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="inspection-notes">Notes</Label>
              <Textarea
                id="inspection-notes"
                placeholder="Optional notes..."
                value={newInspection.notes}
                onChange={(e) => setNewInspection({...newInspection, notes: e.target.value})}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddInspectionDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddInspection}>
              Add Inspection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Construction Phase Dialog */}
      <Dialog open={isEditPhaseDialogOpen} onOpenChange={setIsEditPhaseDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Phase {editingPhase?.phase_number}</DialogTitle>
            <DialogDescription>
              Customize the name and description for this construction phase.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="phase-name">Phase Name</Label>
              <Input
                id="phase-name"
                placeholder="e.g., Framing"
                value={editPhaseData.name}
                onChange={(e) => setEditPhaseData({...editPhaseData, name: e.target.value})}
              />
            </div>
            
            <div>
              <Label htmlFor="phase-description">Description</Label>
              <Textarea
                id="phase-description"
                placeholder="Describe what this phase covers..."
                value={editPhaseData.description}
                onChange={(e) => setEditPhaseData({...editPhaseData, description: e.target.value})}
                rows={3}
              />
              <p className="text-xs text-slate-500 mt-1">
                This description will be visible next to the phase name for easy reference.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditPhaseDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePhaseEdit}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Document Viewer Dialog */}
      <Dialog open={isDocViewerOpen} onOpenChange={setIsDocViewerOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-blue-600" />
              Documents - {viewingItemDocs?.name}
            </DialogTitle>
          </DialogHeader>
          
          {loadingDocs ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : itemDocuments ? (
            <div className="space-y-4">
              {/* Lien Waiver Section */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium flex items-center gap-2 mb-3">
                  <FileText className="w-4 h-4 text-slate-600" />
                  Lien Waiver
                </h4>
                {itemDocuments.lien_waiver ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {itemDocuments.lien_waiver.type === 'e_signature' || itemDocuments.lien_waiver.type === 'e-signed' ? 'E-Signed' : 
                         itemDocuments.lien_waiver.signed ? 'Signed' : 'On File'}
                      </span>
                      {itemDocuments.lien_waiver.signed_at && (
                        <span className="text-xs text-slate-500">
                          on {formatDate(itemDocuments.lien_waiver.signed_at)}
                        </span>
                      )}
                    </div>
                    {itemDocuments.lien_waiver.amount && (
                      <p className="text-sm text-slate-600">Amount: ${itemDocuments.lien_waiver.amount?.toLocaleString()}</p>
                    )}
                    {itemDocuments.lien_waiver.download_url && (
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => openPreview(itemDocuments.lien_waiver.download_url, 'Lien Waiver.pdf')} data-testid="item-doc-view-lien-waiver">
                          <Eye className="w-4 h-4 mr-2" /> View Lien Waiver
                        </Button>
                        <Button size="sm" variant="ghost" asChild>
                          <a href={itemDocuments.lien_waiver.download_url.startsWith('http') ? itemDocuments.lien_waiver.download_url : `${process.env.REACT_APP_BACKEND_URL}${itemDocuments.lien_waiver.download_url}`} download>
                            <Download className="w-4 h-4 mr-2" /> Download
                          </a>
                        </Button>
                      </div>
                    )}
                    {itemDocuments.lien_waiver.signature_data && (
                      <div className="mt-2 border rounded p-2 bg-slate-50">
                        <p className="text-xs text-slate-500 mb-1">Signature:</p>
                        <img 
                          src={itemDocuments.lien_waiver.signature_data} 
                          alt="Contractor Signature" 
                          className="max-h-16 border rounded bg-white"
                        />
                      </div>
                    )}
                  </div>
                ) : itemDocuments.compliance_status?.lien_waiver?.signed ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="text-sm font-medium">Lien Waiver Signed</span>
                    {itemDocuments.compliance_status.lien_waiver.amount && (
                      <span className="text-xs text-slate-500">
                        (${itemDocuments.compliance_status.lien_waiver.amount?.toLocaleString()})
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No lien waiver on file</p>
                )}
              </div>

              {/* Completion Photos Section */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium flex items-center gap-2 mb-3">
                  <Image className="w-4 h-4 text-slate-600" />
                  Completion Photos
                </h4>
                {itemDocuments.completion_photos && itemDocuments.completion_photos.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {itemDocuments.completion_photos.map((photo, idx) => (
                      <div key={idx} className="relative group">
                        <button
                          type="button"
                          onClick={() => openPreview(photo.download_url || (photo.file_id ? `/api/files/${photo.file_id}` : null), photo.filename || `Photo ${idx + 1}`)}
                          className="block w-full"
                          data-testid={`item-doc-photo-${idx}`}
                        >
                          <img 
                            src={photo.download_url || (photo.file_id ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${photo.file_id}` : undefined)} 
                            alt={photo.filename || `Photo ${idx + 1}`} 
                            className="w-full h-32 object-cover rounded border hover:opacity-90 cursor-pointer"
                          />
                        </button>
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 rounded-b">
                          {photo.filename}
                        </div>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="absolute top-1 right-1 h-6 w-6 p-0 bg-white/80 hover:bg-white"
                          asChild
                        >
                          <a href={photo.download_url || (photo.file_id ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${photo.file_id}` : '#')} download={photo.filename}>
                            <Download className="w-3 h-3" />
                          </a>
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : itemDocuments.photos_uploaded_count > 0 || itemDocuments.compliance_status?.completion_photos?.uploaded ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {itemDocuments.photos_uploaded_count || itemDocuments.compliance_status?.completion_photos?.count || 0} photo(s) uploaded
                    </span>
                    <span className="text-xs text-slate-500">(View in contractor portal)</span>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No completion photos on file</p>
                )}
              </div>

              {/* Inspection Report Section */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium flex items-center gap-2 mb-3">
                  <FileCheck className="w-4 h-4 text-slate-600" />
                  Inspection Report
                </h4>
                {itemDocuments.inspection_report ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-sm font-medium">Report on file</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => openPreview(itemDocuments.inspection_report.download_url, 'Inspection Report')} data-testid="item-doc-view-inspection">
                        <Eye className="w-4 h-4 mr-2" /> View Report
                      </Button>
                      <Button size="sm" variant="ghost" asChild>
                        <a href={itemDocuments.inspection_report.download_url.startsWith('http') ? itemDocuments.inspection_report.download_url : `${process.env.REACT_APP_BACKEND_URL}${itemDocuments.inspection_report.download_url}`} download>
                          <Download className="w-4 h-4 mr-2" /> Download
                        </a>
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No inspection report on file</p>
                )}
              </div>

              {/* Receipts/Invoices Section */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium flex items-center gap-2 mb-3">
                  <Receipt className="w-4 h-4 text-slate-600" />
                  Receipts / Invoices
                </h4>
                {itemDocuments.receipts && itemDocuments.receipts.length > 0 ? (
                  <div className="space-y-2">
                    {itemDocuments.receipts.map((receipt, idx) => {
                      const isRefund = receipt.category === 'refund';
                      return (
                        <div key={receipt.id || idx} className={`flex items-center justify-between p-2 rounded ${isRefund ? 'bg-orange-50 border border-orange-200' : 'bg-slate-50'}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {isRefund && <span className="text-[10px] font-bold uppercase tracking-wide bg-orange-200 text-orange-900 px-1.5 py-0.5 rounded">Refund</span>}
                              <span className="text-sm font-medium truncate">{receipt.vendor || receipt.filename || `Receipt ${idx + 1}`}</span>
                            </div>
                            {(receipt.amount || receipt.upload_date) && (
                              <div className="text-xs text-slate-500 mt-0.5">
                                {receipt.amount != null && (
                                  <span className={isRefund ? 'text-orange-700' : 'text-emerald-700'}>
                                    {isRefund ? '−' : ''}${Number(receipt.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                  </span>
                                )}
                                {receipt.amount != null && receipt.upload_date && ' · '}
                                {receipt.upload_date && formatDate(receipt.upload_date)}
                              </div>
                            )}
                          </div>
                          {receipt.download_url && (
                            <Button size="sm" variant="outline" onClick={() => openPreview(receipt.download_url, receipt.vendor || receipt.filename || 'Receipt')} data-testid={`item-doc-view-receipt-${idx}`}>
                              <Eye className="w-4 h-4 mr-1" /> View
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No receipts on file. Use the Pay button to upload receipts when logging a payment.</p>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No documents found for this item</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDocViewerOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Phase Transition Checklist Modal */}
      <PhaseChecklistModal
        isOpen={isPhaseChecklistOpen}
        onClose={() => setIsPhaseChecklistOpen(false)}
        phaseNumber={checklistPhaseNumber}
        phaseName={checklistPhaseName}
        onComplete={() => {
          toast.success(`Phase ${checklistPhaseNumber} checklist completed!`);
        }}
        onSkip={() => {
          // Toast handled inside the modal
        }}
      />

      {/* Pre-Project Expenses Bulk Import Dialog */}
      <PrePaidExpensesDialog
        open={isPrePaidDialogOpen}
        onOpenChange={setIsPrePaidDialogOpen}
        projectId={projectId}
        onComplete={() => {
          if (onRefresh) onRefresh();
        }}
      />

      {/* v2.5.6: Phase-level Inspection Gate dialog (HVAC/Electrical/Plumbing/Concrete/Framing) */}
      <PhaseInspectionsDialog
        open={!!inspectionsPhase}
        onOpenChange={(o) => { if (!o) closePhaseInspections(); }}
        phase={inspectionsPhase}
        onGateChange={onInspectionGateChange}
        onViewFile={(fileId, name) => openPreview(`/api/files/${fileId}`, name || 'Inspection Report')}
      />
      {/* v2.9.14 — single global docs modal, openable from ANY item (card or
          table view, paid or not) via renderDocsBtn → setExpandedDocsPhaseId. */}
      {expandedDocsPhaseId && (() => {
        const docPhase = budgetPhases.find((bp) => bp.id === expandedDocsPhaseId);
        if (!docPhase) return null;
        return (
          <PhaseDocsModalTabs
            isOpen={true}
            phase={docPhase}
            contractor={contractors.find((c) => c.id === docPhase.contractor_id) || (docPhase.contractor_id ? { id: docPhase.contractor_id, name: docPhase.contractor_name || docPhase.assigned_to || 'Contractor' } : null)}
            onClose={() => setExpandedDocsPhaseId(null)}
            onRefresh={onRefresh}
          />
        );
      })()}
      {/* v2.6: in-app document / photo preview */}
      {filePreview && (
        <FilePreviewModal
          url={filePreview.url}
          filename={filePreview.filename}
          onClose={() => setFilePreview(null)}
        />
      )}
    </div>
  );
};

export default ConstructionPhasesV2;
